import { create } from 'zustand';
import { createJSONStorage, persist, subscribeWithSelector } from 'zustand/middleware';
import { getAuthHeaders as getSessionAuthHeaders, useAuthStore } from './authStore';
import { authenticatedFetch } from '../services/authenticatedFetch';
import type { Agent, AgentRuntimeStatus, Attachment, Conversation, ConversationParticipant, ConversationSearchResult, Message, Tag } from '../types';
import { generateConversationTitle, normalizeAgentStatus, normalizeAgentType } from '../utils';
import { getConversationNotificationTag, getConversationNotificationUrl, showNotification } from '../services/notifications';
import {
  buildAsyncJobSummariesByConversation,
  getPendingAsyncJobPlaceholderMessages,
  getAsyncJobMetadata,
  isAsyncJobActiveStatus,
  mergePendingAsyncJobPlaceholders,
  type AsyncJobConversationSummary,
  type AsyncJobMetadata,
} from '../utils/asyncJobs';
import {
  deserializeOfflineAttachments,
  loadOfflineQueue,
  removeOfflineQueueItem,
  serializeOfflineAttachments,
  type OfflineQueueItem,
  upsertOfflineQueueItem,
} from '../services/offlineQueue';

interface MessagePagingState {
  hasMore: boolean;
  nextBefore: string | null;
  total: number;
}

interface ChatState {
  conversations: Conversation[];
  activeConversationId: string | null;
  messages: Record<string, Message[]>;
  // Wave 4: server pages message history (newest window + ?before cursor).
  messagePaging: Record<string, MessagePagingState>;
  loadingOlderMessages: Record<string, boolean>;
  asyncJobSummaries: Record<string, AsyncJobConversationSummary>;
  streamingTokens: Record<string, string[]>;
  streamingMessageIds: Record<string, string>;
  isTyping: boolean;
  typing: Record<string, boolean>;
  agents: Agent[];
  agentStatuses: Record<string, AgentRuntimeStatus>;
  composerDrafts: Record<string, string>;
  suggestionDispatchVersion: Record<string, number>;
  persistedSessionOwnerKey: string | null;
  isLoading: boolean;
  isLoadingAgents: boolean;
  isLoadingConversations: boolean;
  isLoadingTags: boolean;
  isSearchingConversations: boolean;
  error: string | null;
  searchError: string | null;
  voiceEnabled: boolean;
  sidebarConversationsEnabled: boolean;
  favoriteAgentIds: string[];
  tags: Tag[];
  activeTagFilterIds: string[];
  tagMatchMode: 'or' | 'and';
  showArchived: boolean;
  showHidden: boolean;
  searchResults: ConversationSearchResult[];
  searchTotal: number;
  searchHasMore: boolean;
  isOffline: boolean;
  offlineQueueItems: OfflineQueueItem[];
  isFlushingOfflineQueue: boolean;

  setActiveConversation: (conversationId: string | null) => void;
  loadConversations: () => Promise<void>;
  loadMessages: (conversationId: string) => Promise<void>;
  loadOlderMessages: (conversationId: string) => Promise<boolean>;
  loadAgents: () => Promise<void>;
  fetchAgentStatuses: () => Promise<void>;
  loadUserPreferences: () => Promise<void>;
  loadTags: () => Promise<void>;
  setAgents: (agents: Agent[]) => void;
  setFavoriteAgentIds: (agentIds: string[]) => void;
  ensureDefaultConversation: (agentId: string) => void;
  createTag: (input: Pick<Tag, 'name' | 'color'>) => Promise<Tag | null>;
  updateTag: (tagId: string, updates: Partial<Pick<Tag, 'name' | 'color'>>) => Promise<Tag | null>;
  deleteTag: (tagId: string) => Promise<void>;
  setConversationTags: (conversationId: string, tagIds: string[]) => Promise<void>;
  toggleConversationPinned: (conversationId: string) => Promise<void>;
  toggleConversationArchived: (conversationId: string) => Promise<void>;
  hideConversation: (conversationId: string) => Promise<void>;
  unhideConversation: (conversationId: string) => Promise<void>;
  markConversationUnread: (conversationId: string) => Promise<void>;
  toggleActiveTagFilter: (tagId: string) => void;
  clearActiveTagFilters: () => void;
  setTagMatchMode: (mode: 'or' | 'and') => void;
  setShowArchived: (showArchived: boolean) => void;
  setShowHidden: (showHidden: boolean) => void;
  searchConversations: (
    query: string,
    filters?: {
      agentId?: string | null;
      from?: string | null;
      to?: string | null;
      tagIds?: string[];
      tagMatchMode?: 'or' | 'and';
      page?: number;
      append?: boolean;
    },
  ) => Promise<ConversationSearchResult[]>;
  clearSearchResults: () => void;

  sendMessage: (
    conversationId: string,
    content: string,
    attachments?: Array<File | Attachment>,
  ) => Promise<void>;
  initializeOfflineQueue: () => void;
  setOfflineStatus: (isOffline: boolean) => void;
  flushOfflineQueue: () => Promise<void>;
  sendSuggestion: (text: string, conversationId?: string) => void;
  clearComposerDraft: (conversationId: string) => void;
  retryMessage: (messageId: string) => Promise<void>;
  regenerateMessage: (messageId: string) => Promise<void>;
  editMessage: (messageId: string, content: string) => Promise<void>;
  rateMessage: (messageId: string, rating: 'up' | 'down') => Promise<void>;
  stopGenerating: (conversationId: string) => void;
  cancelPendingJob: (jobId: string) => void;
  resumePendingJob: (jobId: string) => void;
  resumePendingJobs: () => void;
  syncActiveJobsFromServer: (options?: { force?: boolean }) => Promise<void>;
  rebuildAsyncJobSummaries: () => void;
  addMessage: (message: Message) => void;
  updateMessage: (messageId: string, updates: Partial<Message>) => void;
  deleteMessage: (messageId: string) => void;
  toggleReaction: (messageId: string, emoji: string) => void;
  togglePin: (messageId: string) => void;

  startStreaming: (conversationId: string, messageId: string) => void;
  addToken: (messageId: string, token: string) => void;
  completeMessage: (messageId: string, conversationId: string, finalContent: string) => void;

  createConversation: (agentId: string, title?: string) => Promise<string>;
  createGroupConversation: (name: string, agentIds: string[]) => Promise<string>;
  convertConversationToGroup: (conversationId: string, name: string, memberEmails: string[]) => Promise<void>;
  renameConversation: (conversationId: string, title: string) => Promise<void>;
  updateConversation: (conversationId: string, updates: Partial<Conversation>) => void;
  deleteConversation: (conversationId: string) => Promise<void>;

  setTyping: {
    (typing: boolean): void;
    (conversationId: string, isTyping: boolean): void;
  };

  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  setVoiceEnabled: (voiceEnabled: boolean) => void;
  setSidebarConversationsEnabled: (enabled: boolean) => void;
  toggleFavoriteAgent: (agentId: string) => Promise<void>;
  updateConversationLastMessage: (message: Message) => void;
}

// D1-11: user-facing fallback identity is the product brand ("Ally"), not the
// Polaris dev-team agent. The id stays 'polaris-orchestrator' — it is the
// registry key of the default routing target, never shown to users.
const DEFAULT_AGENT: Agent = {
  id: 'polaris-orchestrator',
  name: 'Ally',
  description: 'Primary AI assistant',
  type: 'shared',
  status: 'online',
  isOnline: true,
  capabilities: [],
};

const notifiedAssistantMessageIds = new Set<string>();
const pendingUploadFiles = new Map<string, File[]>();
const lastRetryAttemptAt = new Map<string, number>();
const activeStreamControllers = new Map<string, Map<string, AbortController>>();
// Throttle for the server active-jobs sync (resumePendingJobs fires on init,
// rehydrate, focus, online, and after every loadMessages — once per ~10s is
// plenty for a 3s-poll job system).
const ACTIVE_JOBS_SERVER_SYNC_MIN_INTERVAL_MS = 10_000;
let lastActiveJobsServerSyncMs = 0;

const pendingJobPollers = new Map<string, {
  assistantMessageId: string;
  conversationId: string;
  userMessageId?: string | null;
  cancelled: boolean;
  timeoutId?: number;
}>();
const SIDEBAR_CONVERSATIONS_ENABLED_STORAGE_KEY = 'ally_sidebar_conversations_enabled';
const FAVORITE_AGENT_IDS_STORAGE_KEY = 'ally_favorite_agent_ids';
let sessionAsyncBoundaryVersion = 0;

function getSessionAsyncBoundaryVersion() {
  return sessionAsyncBoundaryVersion;
}

function isSessionAsyncBoundaryCurrent(version: number) {
  return version === sessionAsyncBoundaryVersion;
}

function clearSessionScopedAsyncState() {
  sessionAsyncBoundaryVersion += 1;
  notifiedAssistantMessageIds.clear();
  pendingUploadFiles.clear();
  lastRetryAttemptAt.clear();
  activeStreamControllers.forEach((controllers) => controllers.forEach((controller) => controller.abort()));
  activeStreamControllers.clear();
  pendingJobPollers.forEach((_, jobId) => stopPendingJobPolling(jobId));
}

function getSessionOwnerKey(authState: (Pick<ReturnType<typeof useAuthStore.getState>, 'user' | 'impersonatedUser'> & { viewAsUser?: { id?: string; email?: string } | null }) | null | undefined) {
  const viewAsUser = authState?.viewAsUser ?? null;
  const effectiveUser = viewAsUser ?? authState?.impersonatedUser ?? authState?.user;
  if (!effectiveUser) {
    return null;
  }

  const scope = viewAsUser ? 'viewas' : authState?.impersonatedUser ? 'impersonated' : 'user';
  const email = String(effectiveUser.email || '').trim().toLowerCase();
  if (email) {
    return `${scope}:email:${email}`;
  }

  const id = String(effectiveUser.id || '').trim();
  if (id) {
    return `${scope}:id:${id}`;
  }

  return null;
}

function captureOwnerScopedRequestBoundary() {
  const ownerKey = getSessionOwnerKey(useAuthStore.getState());
  const version = getSessionAsyncBoundaryVersion();

  return {
    ownerKey,
    isCurrent() {
      return Boolean(ownerKey)
        && isSessionAsyncBoundaryCurrent(version)
        && getSessionOwnerKey(useAuthStore.getState()) === ownerKey;
    },
  };
}

function getBrowserStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const storage = window.localStorage;
  if (
    storage
    && typeof storage.getItem === 'function'
    && typeof storage.setItem === 'function'
    && typeof storage.removeItem === 'function'
  ) {
    return storage;
  }

  return null;
}

function getStoredSidebarConversationsEnabled() {
  const storage = getBrowserStorage();
  if (!storage) {
    return false;
  }

  const stored = storage.getItem(SIDEBAR_CONVERSATIONS_ENABLED_STORAGE_KEY);
  return stored === 'true';
}

function setStoredSidebarConversationsEnabled(enabled: boolean) {
  const storage = getBrowserStorage();
  if (!storage) {
    return;
  }

  storage.setItem(SIDEBAR_CONVERSATIONS_ENABLED_STORAGE_KEY, String(enabled));
}

function areAgentStatusesEqual(
  left: Record<string, AgentRuntimeStatus>,
  right: Record<string, AgentRuntimeStatus>,
) {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  for (const key of leftKeys) {
    const leftStatus = left[key];
    const rightStatus = right[key];

    if (!rightStatus) {
      return false;
    }

    if (
      leftStatus.status !== rightStatus.status
      || leftStatus.lastCheck !== rightStatus.lastCheck
      || leftStatus.responseTimeMs !== rightStatus.responseTimeMs
    ) {
      return false;
    }
  }

  return true;
}

function normalizeFavoriteAgentIds(input: unknown): string[] {
  if (!Array.isArray(input)) {
    return [];
  }

  return [...new Set(input.map((value) => String(value || '').trim()).filter(Boolean))];
}

function getStoredFavoriteAgentIds() {
  const storage = getBrowserStorage();
  if (!storage) {
    return [];
  }

  try {
    return normalizeFavoriteAgentIds(JSON.parse(storage.getItem(FAVORITE_AGENT_IDS_STORAGE_KEY) || '[]'));
  } catch {
    return [];
  }
}

function setStoredFavoriteAgentIds(agentIds: string[]) {
  const storage = getBrowserStorage();
  if (!storage) {
    return;
  }

  storage.setItem(FAVORITE_AGENT_IDS_STORAGE_KEY, JSON.stringify(normalizeFavoriteAgentIds(agentIds)));
}

function getAuthHeaders(): Record<string, string> {
  return getSessionAuthHeaders({ includeImpersonation: true });
}

function normalizeAttachment(input: any): Attachment {
  return {
    id: String(input?.id ?? input?.attachment_id ?? crypto.randomUUID()),
    messageId: input?.messageId ?? input?.message_id,
    filename: input?.filename ?? 'attachment',
    contentType: input?.contentType ?? input?.content_type ?? input?.mime_type ?? 'application/octet-stream',
    url: input?.url ?? '',
    size: Number(input?.size ?? 0),
    createdAt: input?.createdAt ?? input?.created_at ?? new Date().toISOString(),
    dataUrl: typeof input?.dataUrl === 'string' ? input.dataUrl : undefined,
  };
}

function normalizeSuggestionList(input: unknown): string[] {
  if (!Array.isArray(input)) {
    return [];
  }

  return input
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .slice(0, 3);
}

function normalizeTag(input: any): Tag {
  return {
    id: String(input?.id ?? crypto.randomUUID()),
    userId: input?.userId ?? input?.user_id ?? null,
    userEmail: input?.userEmail ?? input?.user_email ?? null,
    name: String(input?.name ?? 'Tag').trim() || 'Tag',
    color: String(input?.color ?? 'blue').trim().toLowerCase() as Tag['color'],
    createdAt: input?.createdAt ?? input?.created_at ?? new Date().toISOString(),
    updatedAt: input?.updatedAt ?? input?.updated_at ?? new Date().toISOString(),
  };
}

function normalizeMessage(input: any, fallbackConversationId?: string): Message {
  return {
    id: String(input?.id ?? input?.message_id ?? crypto.randomUUID()),
    conversationId: String(input?.conversationId ?? input?.conversation_id ?? fallbackConversationId ?? 'default'),
    userId: input?.userId ?? input?.user_id,
    role: input?.role ?? (input?.sender_kind === 'user' ? 'user' : 'assistant'),
    content: input?.content ?? '',
    attachments: Array.isArray(input?.attachments)
      ? input.attachments.map((attachment: any) => normalizeAttachment(attachment))
      : [],
    metadata: input?.metadata ?? undefined,
    mentions: input?.mentions,
    targetAgentId: input?.targetAgentId ?? input?.target_agent_id,
    isPinned: Boolean(input?.isPinned ?? input?.is_pinned),
    isError: Boolean(input?.isError ?? input?.is_error),
    error: typeof input?.error === 'string' ? input.error : undefined,
    agentId: input?.agentId ?? input?.agent_id,
    user: input?.user ?? (input?.sender_name ? { name: input.sender_name } : undefined),
    createdAt: input?.createdAt ?? input?.created_at ?? input?.timestamp ?? new Date().toISOString(),
    timestamp: input?.timestamp ?? input?.createdAt ?? input?.created_at ?? new Date().toISOString(),
    sentAt: input?.sentAt ?? input?.sent_at,
    deliveredAt: input?.deliveredAt ?? input?.delivered_at,
    status: input?.status,
    reactions: input?.reactions ?? { counts: {}, userReactions: [], reactors: {} },
    suggestions: normalizeSuggestionList(input?.suggestions),
  };
}

function mergeQueuedMessages(messages: Message[], offlineQueueItems: OfflineQueueItem[], conversationId: string): Message[] {
  const queuedMessages = offlineQueueItems
    .filter((item) => item.conversationId === conversationId)
    .map((item) => normalizeMessage({
      id: item.messageId,
      conversationId: item.conversationId,
      role: 'user',
      content: item.content,
      attachments: deserializeOfflineAttachments(item.attachments).map((attachment) => (
        attachment instanceof File ? makeAttachmentFromFile(attachment) : normalizeAttachment(attachment)
      )),
      createdAt: item.createdAt,
      timestamp: item.createdAt,
      status: 'queued' as const,
      metadata: {
        offlineQueueId: item.id,
        queuedAt: item.createdAt,
      },
      user: item.user,
    }, conversationId));

  const queuedById = new Map(queuedMessages.map((message) => [message.id, message]));
  const merged = messages.map((message) => {
    const queued = queuedById.get(message.id);
    if (!queued) {
      return message;
    }

    queuedById.delete(message.id);
    if (message.status === 'queued' || message.status === 'failed') {
      return {
        ...message,
        status: 'queued' as const,
        metadata: {
          ...(message.metadata && typeof message.metadata === 'object' ? message.metadata : {}),
          ...(queued.metadata && typeof queued.metadata === 'object' ? queued.metadata : {}),
        },
      };
    }

    return message;
  });

  return sortMessages([...merged, ...queuedById.values()]);
}

function normalizeConversation(input: any): Conversation {
  const lastMessage = input?.lastMessage ?? input?.last_message;
  const normalizedLastMessage = lastMessage ? normalizeMessage(lastMessage) : undefined;
  const agent = input?.agent;
  const createdAt = input?.createdAt ?? input?.created_at ?? new Date().toISOString();
  const updatedAt =
    input?.updatedAt ??
    input?.updated_at ??
    input?.lastActivity ??
    input?.last_message_at ??
    createdAt;

  return {
    id: String(input?.id ?? input?.conversation_id ?? crypto.randomUUID()),
    title: input?.title ?? input?.groupName ?? input?.group_name ?? agent?.name ?? input?.agentName ?? 'New Chat',
    userId: input?.userId ?? input?.user_id ?? input?.primary_user_id,
    agentId: input?.agentId ?? input?.agent_id ?? agent?.id,
    agentName: input?.agentName ?? input?.agent_name ?? agent?.name,
    agentAvatar: input?.agentAvatar ?? input?.agent_avatar ?? agent?.avatar,
    type: input?.type ?? (input?.kind === 'group' || input?.group_name ? 'GROUP' : 'DIRECT'),
    kind: input?.kind ?? (input?.type === 'GROUP' ? 'group' : '1to1'),
    isGroup: Boolean(input?.isGroup) || input?.type === 'GROUP' || input?.kind === 'group',
    groupName: input?.groupName ?? input?.group_name,
    summary: input?.summary ?? null,
    lastMessage: normalizedLastMessage,
    unreadCount: Number(input?.unreadCount ?? input?.unread_count ?? 0),
    messageCount: Number(input?.messageCount ?? input?.message_count ?? 0) || undefined,
    participantCount: Number(input?.participantCount ?? input?.participant_count ?? 0) || undefined,
    participants: input?.participants,
    sessionKey: input?.sessionKey ?? input?.session_key,
    shareToken: input?.shareToken ?? input?.share_token ?? null,
    pinOrder: input?.pinOrder ?? input?.pin_order ?? null,
    isPinned: Boolean((input?.isPinned ?? input?.is_pinned) ?? ((input?.pinOrder ?? input?.pin_order) !== null && (input?.pinOrder ?? input?.pin_order) !== undefined)),
    archivedAt: input?.archivedAt ?? input?.archived_at ?? null,
    isHidden: Boolean(input?.isHidden ?? input?.is_hidden ?? input?.hiddenAt ?? input?.hidden_at),
    isUnread: Boolean(input?.isUnread ?? input?.is_unread ?? input?.markUnread ?? input?.mark_unread),
    isDeleted: Boolean(input?.isDeleted ?? input?.is_deleted),
    tags: Array.isArray(input?.tags) ? input.tags.map((tag: any) => normalizeTag(tag)) : [],
    createdAt,
    updatedAt,
    lastActivity: updatedAt,
  };
}

function normalizeConversationSearchResult(input: any): ConversationSearchResult {
  return {
    conversationId: String(input?.conversationId ?? input?.conversation_id ?? ''),
    conversationTitle: String(input?.conversationTitle ?? input?.conversation_title ?? 'Conversation'),
    conversationUpdatedAt: input?.conversationUpdatedAt ?? input?.conversation_updated_at ?? new Date().toISOString(),
    conversationArchivedAt: input?.conversationArchivedAt ?? input?.conversation_archived_at ?? null,
    messageId: String(input?.messageId ?? input?.message_id ?? crypto.randomUUID()),
    messageCreatedAt: input?.messageCreatedAt ?? input?.message_created_at ?? new Date().toISOString(),
    snippet: String(input?.snippet ?? ''),
    highlightedSnippet: typeof input?.highlightedSnippet === 'string' ? input.highlightedSnippet : input?.highlighted_snippet,
    agentId: input?.agentId ?? input?.agent_id ?? null,
    agentName: input?.agentName ?? input?.agent_name ?? null,
    tags: Array.isArray(input?.tags) ? input.tags.map((tag: any) => normalizeTag(tag)) : [],
    rank: typeof input?.rank === 'number' ? input.rank : undefined,
  };
}

function syncConversationTags(conversations: Conversation[], tags: Tag[]) {
  const tagsById = new Map(tags.map((tag) => [tag.id, tag]));
  return conversations.map((conversation) => {
    if (!conversation.tags?.length) {
      return conversation;
    }

    return {
      ...conversation,
      tags: conversation.tags
        .map((tag) => tagsById.get(tag.id) ?? tag)
        .filter((tag): tag is Tag => Boolean(tag)),
    };
  });
}

function _getConversationTagIds(conversation: Conversation | null | undefined) {
  return (conversation?.tags ?? []).map((tag) => tag.id);
}

function buildConversationTags(tagIds: string[], tags: Tag[]) {
  const tagsById = new Map(tags.map((tag) => [tag.id, tag]));
  return [...new Set(tagIds)].map((tagId) => tagsById.get(tagId)).filter((tag): tag is Tag => Boolean(tag));
}

function normalizeConversationApiPayload(data: unknown, fallbackConversation?: Conversation) {
  const rawConversation = data && typeof data === 'object'
    ? ('data' in data ? (data as { data?: unknown }).data : 'conversation' in data ? (data as { conversation?: unknown }).conversation : data)
    : data;

  return normalizeConversation(rawConversation ?? fallbackConversation ?? {});
}

function normalizeTagApiPayload(data: unknown, fallbackTag?: Tag) {
  const rawTag = data && typeof data === 'object'
    ? ('data' in data ? (data as { data?: unknown }).data : 'tag' in data ? (data as { tag?: unknown }).tag : data)
    : data;

  return normalizeTag(rawTag ?? fallbackTag ?? {});
}

function normalizeSearchResultsPayload(data: unknown) {
  if (!data || typeof data !== 'object' || !('data' in data)) {
    return { results: [] as ConversationSearchResult[], total: 0, hasMore: false };
  }

  const payload = (data as { data?: any }).data;
  const rawResults = Array.isArray(payload?.results) ? payload.results : [];
  return {
    results: rawResults.map((result: any) => normalizeConversationSearchResult(result)),
    total: Number(payload?.total ?? rawResults.length ?? 0),
    hasMore: Boolean(payload?.hasMore),
  };
}

function normalizeAgent(input: any): Agent {
  // D7-08: collapse mixed-case API values onto the lowercase unions here, at
  // the boundary — downstream code compares lowercase literals only.
  const rawStatus = input?.healthStatus ?? input?.health_status ?? input?.status;
  const status = normalizeAgentStatus(rawStatus ?? 'online');
  return {
    id: String(input?.id ?? input?.agentId ?? input?.agent_id ?? 'polaris-orchestrator'),
    name: input?.name ?? 'Unknown Agent',
    description: input?.description ?? 'AI assistant',
    avatar: input?.avatar,
    type: normalizeAgentType(input?.type),
    gatewayHost: input?.gatewayHost,
    gatewayPort: input?.gatewayPort,
    status,
    healthStatus: normalizeAgentStatus(input?.healthStatus ?? input?.health_status ?? status),
    capabilities: Array.isArray(input?.capabilities)
      ? input.capabilities
      : input?.model
        ? [input.model]
        : [],
    owner: input?.owner,
    ownerName: input?.ownerName ?? input?.owner,
    machineId: input?.machineId,
    model: input?.model,
    isActive: input?.isActive ?? input?.enabled ?? true,
    isOnline: input?.isOnline ?? ['online', 'healthy', 'degraded', 'slow'].includes(status),
    lastSeen: input?.lastSeen ?? input?.lastCheck ?? input?.last_health_check,
    responseTimeMs: typeof input?.responseTimeMs === 'number' ? input.responseTimeMs : null,
    createdAt: input?.createdAt ?? input?.created_at,
    updatedAt: input?.updatedAt ?? input?.updated_at,
  };
}

function makeAttachmentFromFile(file: File): Attachment {
  return {
    id: crypto.randomUUID(),
    filename: file.name,
    contentType: file.type || 'application/octet-stream',
    url: URL.createObjectURL(file),
    size: file.size,
    createdAt: new Date().toISOString(),
  };
}

function getMessageTimestamp(message: Message): string {
  return message.createdAt ?? message.timestamp ?? new Date().toISOString();
}

function sortMessages(messages: Message[]) {
  return [...messages].sort(
    (left, right) => new Date(getMessageTimestamp(left)).getTime() - new Date(getMessageTimestamp(right)).getTime(),
  );
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function registerStreamController(conversationId: string, assistantMessageId: string, controller: AbortController) {
  const existing = activeStreamControllers.get(conversationId) ?? new Map<string, AbortController>();
  existing.set(assistantMessageId, controller);
  activeStreamControllers.set(conversationId, existing);
}

function unregisterStreamController(conversationId: string, assistantMessageId: string) {
  const existing = activeStreamControllers.get(conversationId);
  if (!existing) {
    return;
  }

  existing.delete(assistantMessageId);
  if (existing.size === 0) {
    activeStreamControllers.delete(conversationId);
  }
}

function abortConversationStreams(conversationId: string) {
  const controllers = activeStreamControllers.get(conversationId);
  if (!controllers) {
    return;
  }

  controllers.forEach((controller) => controller.abort());
}

function isAbortError(error: unknown) {
  return error instanceof DOMException
    ? error.name === 'AbortError'
    : typeof error === 'object' && error !== null && 'name' in error && (error as { name?: string }).name === 'AbortError';
}

async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error(`Failed to read ${file.name}`));
    };
    reader.onerror = () => reject(reader.error ?? new Error(`Failed to read ${file.name}`));
    reader.readAsDataURL(file);
  });
}

async function buildRequestAttachments(uploadedAttachments: Attachment[], files: File[], extraAttachments: Attachment[] = []): Promise<Attachment[]> {
  const inlineAttachments = await Promise.all(uploadedAttachments.map(async (attachment, index) => {
    const file = files[index];
    if (!file || !file.type.startsWith('image/')) {
      return attachment;
    }

    try {
      return {
        ...attachment,
        dataUrl: await fileToDataUrl(file),
      };
    } catch (error) {
      console.warn(`Failed to inline attachment ${file.name}:`, error);
      return attachment;
    }
  }));

  return [...inlineAttachments, ...extraAttachments];
}

async function uploadFiles(files: File[]): Promise<Attachment[]> {
  const uploaded: Attachment[] = [];

  for (const file of files) {
    const formData = new FormData();
    formData.append('file', file, file.name);

    const response = await authenticatedFetch('/api/files/upload', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });

    const data = await response.json().catch(() => null);
    if (!response.ok || !data?.success) {
      throw new Error(data?.error || `Failed to upload ${file.name}`);
    }

    uploaded.push(normalizeAttachment(data.data));
  }

  return uploaded;
}

function buildHistory(messages: Message[]) {
  const recent = messages.slice(-20);
  return recent.map((message) => ({
    role: message.role === 'tool' ? 'assistant' : message.role,
    content: message.content,
  }));
}

function getConversationById(conversations: Conversation[], conversationId: string) {
  return conversations.find((conversation) => conversation.id === conversationId) ?? null;
}

function findMessageById(messagesByConversation: Record<string, Message[]>, messageId: string) {
  for (const [conversationId, messages] of Object.entries(messagesByConversation)) {
    const message = messages.find((entry) => entry.id === messageId);
    if (message) {
      return { conversationId, message };
    }
  }

  return null;
}

function findMessageByJobId(messagesByConversation: Record<string, Message[]>, jobId: string) {
  for (const [conversationId, messages] of Object.entries(messagesByConversation)) {
    for (const message of messages) {
      const asyncJob = getAsyncJobMetadata(message);
      if (asyncJob?.jobId === jobId) {
        return { conversationId, message, asyncJob };
      }
    }
  }

  return null;
}

function isAsyncJobProcessingResponse(data: unknown) {
  return Boolean(
    data
    && typeof data === 'object'
    && 'status' in data
    && (data as Record<string, unknown>).status === 'processing'
    && 'jobId' in data
    && typeof (data as Record<string, unknown>).jobId === 'string',
  );
}

function getPendingAsyncJobEntries(messagesByConversation: Record<string, Message[]>) {
  return Object.values(messagesByConversation).flatMap((messages) => messages
    .map((message) => ({
      message,
      asyncJob: getAsyncJobMetadata(message),
    }))
    .filter((entry): entry is { message: Message; asyncJob: AsyncJobMetadata } => Boolean(entry.asyncJob && isAsyncJobActiveStatus(entry.asyncJob.status))));
}

function getRetainedAsyncJobMessages(messagesByConversation: Record<string, Message[]>) {
  return Object.entries(messagesByConversation).reduce<Record<string, Message[]>>((retained, [conversationId, messages]) => {
    const retainedMessages = getPendingAsyncJobPlaceholderMessages(messages);
    if (retainedMessages.length > 0) {
      retained[conversationId] = retainedMessages;
    }
    return retained;
  }, {});
}

function hasRetainedAsyncJobMessages(messagesByConversation: Record<string, Message[]>) {
  return Object.keys(messagesByConversation).length > 0;
}

function areAsyncJobSummariesEqual(
  left: Record<string, AsyncJobConversationSummary>,
  right: Record<string, AsyncJobConversationSummary>,
) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function buildAuthBoundaryResetState(
  retainedMessages: Record<string, Message[]>,
  persistedSessionOwnerKey: string | null,
) {
  return {
    conversations: [],
    messages: retainedMessages,
    asyncJobSummaries: buildAsyncJobSummariesByConversation(retainedMessages),
    streamingTokens: {},
    streamingMessageIds: {},
    isTyping: false,
    typing: {},
    activeConversationId: null,
    agents: [],
    agentStatuses: {},
    composerDrafts: {},
    suggestionDispatchVersion: {},
    persistedSessionOwnerKey,
    isLoading: false,
    isLoadingAgents: false,
    isLoadingConversations: false,
    isLoadingTags: false,
    isSearchingConversations: false,
    error: null,
    searchError: null,
    favoriteAgentIds: [],
    tags: [],
    activeTagFilterIds: [],
    tagMatchMode: 'or' as const,
    showArchived: false,
    showHidden: false,
    searchResults: [],
    searchTotal: 0,
    searchHasMore: false,
    isOffline: false,
    offlineQueueItems: [],
    isFlushingOfflineQueue: false,
  };
}

function maybeNotifyAssistantMessage(
  message: Message,
  conversations: Conversation[],
  agents: Agent[],
  activeConversationId: string | null,
) {
  if (typeof window === 'undefined') {
    return;
  }

  if (getAsyncJobMetadata(message)) {
    return;
  }

  if (message.role !== 'assistant' || message.status === 'streaming' || message.status === 'failed' || message.isError) {
    return;
  }

  const content = String(message.content || '').trim();
  if (!content || notifiedAssistantMessageIds.has(message.id)) {
    return;
  }

  notifiedAssistantMessageIds.add(message.id);
  const conversation = getConversationById(conversations, message.conversationId);
  const sender = agents.find((agent) => agent.id === message.agentId)?.name
    || conversation?.agentName
    || conversation?.groupName
    || conversation?.title
    || 'Ally';
  const isOtherConversation = Boolean(activeConversationId && activeConversationId !== message.conversationId);

  // D7-05 (FE-6.4): tag notifications per conversation so replies from
  // different conversations stack instead of replacing each other, and carry
  // a deep-link URL so the notification (or its Open action) lands on the
  // right conversation.
  const notificationConfig = {
    tag: getConversationNotificationTag(message.conversationId),
    url: getConversationNotificationUrl(message.conversationId),
  };

  if (isOtherConversation) {
    showNotification('New reply', `${sender} responded in your other conversation`, undefined, {
      ...notificationConfig,
      force: true,
    });
    return;
  }

  showNotification(sender, content.slice(0, 160), undefined, notificationConfig);
}

function getGroupAgentIds(conversation: Conversation | null): string[] {
  return (conversation?.participants ?? [])
    .filter((participant) => participant.participantType === 'AGENT')
    .map((participant) => participant.participantId);
}

function extractMentionedAgentIds(content: string, conversation: Conversation | null, agents: Agent[]) {
  const normalized = content.toLowerCase();
  const available = new Set(getGroupAgentIds(conversation));

  return agents
    .filter((agent) => available.has(agent.id))
    .filter((agent) => new RegExp(`(^|\\s)@${escapeRegExp(agent.name.toLowerCase())}(?=\\s|$|[.,!?])`).test(normalized))
    .map((agent) => agent.id);
}

async function readApiPayload(response: Response) {
  if (typeof response.text === 'function') {
    const text = await response.text().catch(() => '');
    if (!text) {
      return null;
    }

    try {
      return JSON.parse(text) as Record<string, unknown>;
    } catch {
      return text;
    }
  }

  if (typeof response.json === 'function') {
    return response.json().catch(() => null);
  }

  return null;
}

function getApiErrorMessage(data: unknown) {
  if (typeof data === 'string' && data.trim()) {
    return data;
  }

  if (data && typeof data === 'object' && 'error' in data && typeof data.error === 'string' && data.error.trim()) {
    return data.error;
  }

  return 'Failed to send message';
}

function extractAssistantContentFromReply(data: any) {
  if (!data || typeof data !== 'object') {
    return '';
  }

  if (typeof data.message === 'string') {
    return data.message;
  }

  if (typeof data.content === 'string') {
    return data.content;
  }

  return '';
}

function extractRoutingSuggestionFromReply(data: any) {
  if (!data || typeof data !== 'object' || !data.routingSuggestion || typeof data.routingSuggestion !== 'object') {
    return undefined;
  }

  return data.routingSuggestion;
}

function stripAsyncJobMetadata(metadata: Message['metadata']) {
  const nextMetadata = metadata && typeof metadata === 'object'
    ? { ...(metadata as Record<string, unknown>) }
    : {};

  delete nextMetadata.asyncJob;
  return nextMetadata;
}

function buildCompletedAsyncJobMessage({
  assistantMessageId,
  conversationId,
  placeholderMessage,
  response,
}: {
  assistantMessageId: string;
  conversationId: string;
  placeholderMessage?: Message;
  response: Record<string, any> | null | undefined;
}) {
  const content = extractAssistantContentFromReply(response);
  if (!content.trim()) {
    return null;
  }

  const routingSuggestion = extractRoutingSuggestionFromReply(response);
  const metadata = {
    ...stripAsyncJobMetadata(placeholderMessage?.metadata),
    ...(routingSuggestion ? { routingSuggestion } : {}),
  };
  const createdAt = placeholderMessage?.createdAt
    ?? placeholderMessage?.timestamp
    ?? (typeof response?.createdAt === 'string' ? response.createdAt : undefined)
    ?? (typeof response?.deliveredAt === 'string' ? response.deliveredAt : undefined)
    ?? new Date().toISOString();

  return normalizeMessage({
    id: assistantMessageId,
    conversationId,
    role: 'assistant',
    content,
    createdAt,
    timestamp: placeholderMessage?.timestamp ?? placeholderMessage?.createdAt ?? createdAt,
    deliveredAt: typeof response?.deliveredAt === 'string' ? response.deliveredAt : undefined,
    status: 'sent',
    agentId: response?.agentId ?? placeholderMessage?.agentId,
    metadata,
    suggestions: Array.isArray(response?.suggestions) ? response.suggestions : [],
    reactions: placeholderMessage?.reactions ?? { counts: {}, userReactions: [], reactors: {} },
  }, conversationId);
}

async function requestAgentReply({
  message,
  agentId,
  conversationId,
  history,
  messageId,
  attachments,
  userMessage,
  assistantMessageId,
  conversation,
}: {
  message: string;
  agentId: string;
  conversationId: string;
  history: Array<{ role: string; content: string }>;
  messageId?: string;
  attachments?: Attachment[];
  userMessage?: Message;
  assistantMessageId?: string;
  conversation?: Conversation | null;
}) {
  const response = await authenticatedFetch('/api/chat', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({
      message,
      agentId,
      conversationId,
      history,
      messageId,
      attachments,
      userMessage,
      assistantMessageId,
      conversation,
    }),
  });

  const data = await readApiPayload(response);
  if (!response.ok || !data || typeof data === 'string' || data.ok !== true) {
    throw new Error(getApiErrorMessage(data));
  }

  return data;
}

async function requestAgentReplyWithJob({
  message,
  agentId,
  conversationId,
  history,
  messageId,
  attachments,
  userMessage,
  assistantMessageId,
  conversation,
}: {
  message: string;
  agentId: string;
  conversationId: string;
  history: Array<{ role: string; content: string }>;
  messageId?: string;
  attachments?: Attachment[];
  userMessage?: Message;
  assistantMessageId?: string;
  conversation?: Conversation | null;
}) {
  const response = await authenticatedFetch('/api/chat', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({
      message,
      agentId,
      conversationId,
      history,
      messageId,
      attachments,
      userMessage,
      assistantMessageId,
      conversation,
    }),
  });

  const data = await readApiPayload(response);
  if (response.status === 202 && isAsyncJobProcessingResponse(data)) {
    return data;
  }

  if (!response.ok || !data || typeof data === 'string' || data.ok !== true) {
    throw new Error(getApiErrorMessage(data));
  }

  return data;
}

// Async long-run refresh resilience: the authoritative list of the caller's
// RUNNING jobs lives on the server (GET /api/jobs), not in this client's
// memory. A fresh client (page refresh, second device, cleared storage)
// rebuilds its in-conversation status cards from this listing.
async function requestActiveJobs(): Promise<Record<string, any>[]> {
  const response = await authenticatedFetch('/api/jobs', {
    headers: getAuthHeaders(),
  });

  const data = await readApiPayload(response);
  if (!response.ok || !data || typeof data !== 'object' || !Array.isArray((data as Record<string, any>).jobs)) {
    return [];
  }

  return ((data as Record<string, any>).jobs as unknown[]).filter(
    (job): job is Record<string, any> => Boolean(job && typeof job === 'object'),
  );
}

async function requestAgentJobStatus(jobId: string) {
  const response = await authenticatedFetch(`/api/jobs/${jobId}`, {
    headers: getAuthHeaders(),
  });

  const data = await readApiPayload(response);

  // 2026-08-13 fix: a 404 `{status:'missing'}` means the job is GONE (server
  // restarted and the job never reached SQL, or it expired). Previously this
  // threw, landed in the poller's generic catch, and the client re-polled a
  // dead job forever ("Still working, reconnecting..."). Return it so the
  // poller can surface a terminal error instead.
  if (response.status === 404 && data && typeof data === 'object' && (data as Record<string, any>).status === 'missing') {
    return data as Record<string, any>;
  }

  if (!response.ok || !data || typeof data === 'string') {
    throw new Error(getApiErrorMessage(data));
  }

  return data as Record<string, any>;
}

function upsertLocalConversationMessage(
  set: (partial: any) => void,
  conversationId: string,
  message: Message,
) {
  const normalized = normalizeMessage(message, conversationId);

  set((state: ChatState) => {
    const conversationMessages = state.messages[conversationId] || [];
    const existingIndex = conversationMessages.findIndex((item) => item.id === normalized.id);
    let nextMessages: Message[];

    if (existingIndex >= 0) {
      nextMessages = [...conversationMessages];
      nextMessages[existingIndex] = {
        ...nextMessages[existingIndex],
        ...normalized,
      };
    } else {
      nextMessages = sortMessages([...conversationMessages, normalized]);
    }

    return {
      messages: {
        ...state.messages,
        [conversationId]: nextMessages,
      },
    };
  });
}

function patchLocalMessage(
  set: (partial: any) => void,
  messageId: string,
  updates: Partial<Message>,
) {
  set((state: ChatState) => {
    const nextMessages = { ...state.messages };

    for (const conversationId of Object.keys(nextMessages)) {
      const items = nextMessages[conversationId];
      const index = items.findIndex((message) => message.id === messageId);
      if (index >= 0) {
        const merged = normalizeMessage({ ...items[index], ...updates }, conversationId);
        nextMessages[conversationId] = [...items];
        nextMessages[conversationId][index] = merged;
        break;
      }
    }

    return { messages: nextMessages };
  });
}

function _removeLocalMessage(set: (partial: any) => void, messageId: string) {
  set((state: ChatState) => {
    const nextMessages = { ...state.messages };
    for (const conversationId of Object.keys(nextMessages)) {
      const filtered = nextMessages[conversationId].filter((message) => message.id !== messageId);
      if (filtered.length !== nextMessages[conversationId].length) {
        nextMessages[conversationId] = filtered;
        break;
      }
    }
    return { messages: nextMessages };
  });
}

function stopPendingJobPolling(jobId: string) {
  if (typeof window === 'undefined') {
    pendingJobPollers.delete(jobId);
    return;
  }

  const existing = pendingJobPollers.get(jobId);
  if (existing?.timeoutId) {
    window.clearTimeout(existing.timeoutId);
  }
  pendingJobPollers.delete(jobId);
}

function startPendingJobPolling({
  get,
  set,
  jobId,
  assistantMessageId,
  conversationId,
  userMessageId,
}: {
  get: () => ChatState;
  set: (partial: any) => void;
  jobId: string;
  assistantMessageId: string;
  conversationId: string;
  userMessageId?: string | null;
}) {
  stopPendingJobPolling(jobId);

  const requestSessionAsyncBoundaryVersion = getSessionAsyncBoundaryVersion();
  const isRequestSessionCurrent = () => isSessionAsyncBoundaryCurrent(requestSessionAsyncBoundaryVersion);

  const poller = {
    assistantMessageId,
    conversationId,
    userMessageId,
    cancelled: false,
    timeoutId: undefined as number | undefined,
  };
  pendingJobPollers.set(jobId, poller);

  const scheduleNextPoll = () => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!isRequestSessionCurrent()) {
      return;
    }

    const current = pendingJobPollers.get(jobId);
    if (!current || current.cancelled) {
      return;
    }

    current.timeoutId = window.setTimeout(() => {
      void poll();
    }, 3_000);
  };

  const poll = async () => {
    if (!isRequestSessionCurrent()) {
      return;
    }

    const current = pendingJobPollers.get(jobId);
    if (!current || current.cancelled) {
      return;
    }

    try {
      const data = await requestAgentJobStatus(jobId);
      if (!isRequestSessionCurrent()) {
        return;
      }

      const activePoller = pendingJobPollers.get(jobId);
      if (!activePoller || activePoller.cancelled) {
        return;
      }

      const placeholderEntry = findMessageById(get().messages, assistantMessageId);
      const existingAsyncJob = getAsyncJobMetadata(placeholderEntry?.message);

      // Terminal: the server no longer knows this job. Stop polling and tell
      // the user to resend instead of spinning forever.
      if (data.status === 'missing') {
        stopPendingJobPolling(jobId);
        const lostMessage = 'This response was lost — the server may have restarted while processing it. Please send your message again.';
        get().setTyping(conversationId, false);
        if (placeholderEntry?.message) {
          patchLocalMessage(set, assistantMessageId, {
            metadata: {
              ...(placeholderEntry.message.metadata && typeof placeholderEntry.message.metadata === 'object' ? placeholderEntry.message.metadata : {}),
              asyncJob: {
                ...(existingAsyncJob ?? {
                  jobId,
                  status: 'failed',
                  progressMessage: lostMessage,
                  startedAt: new Date().toISOString(),
                  elapsedMs: 0,
                }),
                jobId,
                status: 'failed',
                progressMessage: lostMessage,
                startedAt: existingAsyncJob?.startedAt || new Date().toISOString(),
                elapsedMs: existingAsyncJob?.elapsedMs || 0,
                userMessageId: userMessageId || existingAsyncJob?.userMessageId || null,
                assistantMessageId,
              },
            },
          });
        }
        if (userMessageId) {
          get().updateMessage(userMessageId, {
            status: 'failed',
            error: lostMessage,
          });
        }
        get().setError(lostMessage);
        return;
      }

      if (data.status === 'processing') {
        if (placeholderEntry?.message) {
          patchLocalMessage(set, assistantMessageId, {
            metadata: {
              ...(placeholderEntry.message.metadata && typeof placeholderEntry.message.metadata === 'object' ? placeholderEntry.message.metadata : {}),
              asyncJob: {
                ...(existingAsyncJob ?? {
                  jobId,
                  status: 'processing',
                  progressMessage: 'Thinking...',
                  startedAt: new Date().toISOString(),
                  elapsedMs: 0,
                }),
                jobId,
                status: 'processing',
                progressMessage: typeof data.message === 'string' && data.message.trim()
                  ? data.message
                  : existingAsyncJob?.progressMessage || 'Thinking...',
                startedAt: typeof data.startedAt === 'string' && data.startedAt.trim()
                  ? data.startedAt
                  : existingAsyncJob?.startedAt || new Date().toISOString(),
                elapsedMs: Math.max(0, Number(data.elapsedMs || 0) || 0),
                userMessageId: userMessageId || existingAsyncJob?.userMessageId || null,
                assistantMessageId,
              },
            },
          });
        }
        scheduleNextPoll();
        return;
      }

      stopPendingJobPolling(jobId);

      if (data.status === 'completed') {
        const deliveredAt = typeof data.response?.deliveredAt === 'string' ? data.response.deliveredAt : new Date().toISOString();
        const completedMessage = buildCompletedAsyncJobMessage({
          assistantMessageId,
          conversationId,
          placeholderMessage: placeholderEntry?.message,
          response: data.response && typeof data.response === 'object' ? data.response as Record<string, any> : null,
        });

        if (completedMessage) {
          get().addMessage(completedMessage);
        } else if (placeholderEntry?.message) {
          patchLocalMessage(set, assistantMessageId, {
            metadata: {
              ...(placeholderEntry.message.metadata && typeof placeholderEntry.message.metadata === 'object' ? placeholderEntry.message.metadata : {}),
              asyncJob: {
                ...(existingAsyncJob ?? {
                  jobId,
                  status: 'completed',
                  progressMessage: 'Response ready.',
                  startedAt: new Date().toISOString(),
                  elapsedMs: 0,
                }),
                jobId,
                status: 'completed',
                progressMessage: 'Response ready. Refreshing the chat…',
                startedAt: existingAsyncJob?.startedAt || new Date().toISOString(),
                elapsedMs: Math.max(0, Number(data.elapsedMs || existingAsyncJob?.elapsedMs || 0) || 0),
                userMessageId: userMessageId || existingAsyncJob?.userMessageId || null,
                assistantMessageId,
              },
            },
          });
        }

        get().setTyping(conversationId, false);
        if (userMessageId) {
          get().updateMessage(userMessageId, {
            status: 'delivered',
            deliveredAt,
            error: undefined,
          });
        }
        if (data.response?.sessionKey) {
          get().updateConversation(conversationId, { sessionKey: String(data.response.sessionKey) });
        }
        await get().loadMessages(conversationId);
        // D6-12: async job finished the opening exchange — try an LLM title.
        void maybeAutoTitleConversation({ get, conversationId });
        return;
      }

      get().setTyping(conversationId, false);
      patchLocalMessage(set, assistantMessageId, {
        metadata: {
          ...(placeholderEntry?.message?.metadata && typeof placeholderEntry.message.metadata === 'object' ? placeholderEntry.message.metadata : {}),
          asyncJob: {
            ...(existingAsyncJob ?? {
              jobId,
              status: 'failed',
              progressMessage: 'Ally could not finish this job.',
              startedAt: new Date().toISOString(),
              elapsedMs: 0,
            }),
            jobId,
            status: data.status === 'cancelled' ? 'cancelled' : 'failed',
            progressMessage: typeof data.error === 'string' && data.error.trim()
              ? data.error
              : data.status === 'cancelled'
                ? 'Updates paused on this device. The job may still finish in the background.'
                : 'Ally could not finish this job.',
            startedAt: existingAsyncJob?.startedAt || new Date().toISOString(),
            elapsedMs: Math.max(0, Number(data.elapsedMs || existingAsyncJob?.elapsedMs || 0) || 0),
            userMessageId: userMessageId || existingAsyncJob?.userMessageId || null,
            assistantMessageId,
          },
        },
      });
      if (userMessageId) {
        get().updateMessage(userMessageId, {
          status: 'failed',
          error: typeof data.error === 'string' && data.error.trim() ? data.error : 'Failed to send message',
        });
      }
      get().setError(typeof data.error === 'string' && data.error.trim() ? data.error : 'Failed to send message');
    } catch {
      const currentError = pendingJobPollers.get(jobId);
      if (!currentError || currentError.cancelled || !isRequestSessionCurrent()) {
        return;
      }

      const placeholderEntry = findMessageById(get().messages, assistantMessageId);
      const existingAsyncJob = getAsyncJobMetadata(placeholderEntry?.message);
      if (placeholderEntry?.message) {
        patchLocalMessage(set, assistantMessageId, {
          metadata: {
            ...(placeholderEntry.message.metadata && typeof placeholderEntry.message.metadata === 'object' ? placeholderEntry.message.metadata : {}),
            asyncJob: {
              ...(existingAsyncJob ?? {
                jobId,
                status: 'processing',
                progressMessage: 'Thinking...',
                startedAt: new Date().toISOString(),
                elapsedMs: 0,
              }),
              jobId,
              status: 'processing',
              progressMessage: 'Still working, reconnecting for an update...',
              startedAt: existingAsyncJob?.startedAt || new Date().toISOString(),
              elapsedMs: existingAsyncJob?.elapsedMs || 0,
              userMessageId: userMessageId || existingAsyncJob?.userMessageId || null,
              assistantMessageId,
            },
          },
        });
      }
      scheduleNextPoll();
    }
  };

  void poll();
}

async function requestAgentReplyStream({
  message,
  agentId,
  conversationId,
  history,
  onDelta,
  onStatusChange,
  onDelivered,
  messageId,
  attachments,
  userMessage,
  assistantMessageId,
  conversation,
  signal,
}: {
  message: string;
  agentId: string;
  conversationId: string;
  history: Array<{ role: string; content: string }>;
  onDelta: (delta: string) => void;
  onStatusChange?: (status: 'starting' | 'streaming' | 'recovering') => void;
  onDelivered?: (deliveredAt?: string) => void;
  messageId?: string;
  attachments?: Attachment[];
  userMessage?: Message;
  assistantMessageId?: string;
  conversation?: Conversation | null;
  signal?: AbortSignal;
}) {
  const requestPayload = {
    message,
    agentId,
    conversationId,
    history,
    messageId,
    attachments,
    userMessage,
    assistantMessageId,
    conversation,
  };

  const streamOnce = async () => {
    let response: Response;

    try {
      response = await authenticatedFetch('/api/chat?stream=true', {
        method: 'POST',
        headers: {
          Accept: 'text/event-stream',
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(requestPayload),
        signal,
      });
    } catch (error) {
      if (signal?.aborted || isAbortError(error)) {
        return { fullContent: '', sessionKey: undefined, deliveredAt: undefined, isDone: true, aborted: true };
      }
      throw error;
    }

    if (!response.ok) {
      const data = await readApiPayload(response);
      throw new Error(getApiErrorMessage(data));
    }

    const sessionKey = response.headers.get('x-session-key') || undefined;
    const deliveredAt = response.headers.get('x-delivered-at') || undefined;
    onDelivered?.(deliveredAt);
    if (!response.body) {
      return { fullContent: '', sessionKey, deliveredAt, isDone: true, aborted: false };
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullContent = '';
    let isDone = false;
    let sawContent = false;

    const processEvent = (eventText: string) => {
      const data = eventText
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trimStart())
        .join('\n')
        .trim();

      if (!data) {
        return;
      }

      if (data === '[DONE]') {
        isDone = true;
        return;
      }

      try {
        const parsed = JSON.parse(data) as {
          choices?: Array<{
            delta?: { content?: string };
            message?: { content?: string };
          }>;
          ally_event?: { type?: string; message?: string };
        };

        // Wave 4: server emits a trailing persist_error event (before [DONE])
        // when the exchange streamed fine but could not be saved to history.
        if (parsed.ally_event?.type === 'persist_error') {
          const warning = parsed.ally_event.message || 'This response could not be saved to history.';
          console.warn('Stream persist error:', warning);
          useChatStore.setState({ error: warning });
          return;
        }

        const delta = parsed.choices?.[0]?.delta?.content;
        const fallbackContent = fullContent.length === 0 ? parsed.choices?.[0]?.message?.content : undefined;
        const nextChunk = typeof delta === 'string' ? delta : typeof fallbackContent === 'string' ? fallbackContent : '';

        if (nextChunk) {
          if (!sawContent) {
            sawContent = true;
            onStatusChange?.('streaming');
          }
          fullContent += nextChunk;
          onDelta(nextChunk);
        }
      } catch (error) {
        console.warn('Failed to parse streaming response chunk', error);
      }
    };

    try {
      while (!isDone) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true }).replace(/\r/g, '');
        let boundary = buffer.indexOf('\n\n');
        while (boundary >= 0) {
          processEvent(buffer.slice(0, boundary));
          buffer = buffer.slice(boundary + 2);
          boundary = buffer.indexOf('\n\n');
        }
      }
    } catch (error) {
      if (signal?.aborted || isAbortError(error)) {
        return { fullContent, sessionKey, deliveredAt, isDone: true, aborted: true };
      }
      throw error;
    }

    const flushed = decoder.decode().replace(/\r/g, '');
    if (flushed) {
      buffer += flushed;
    }
    if (buffer.trim()) {
      processEvent(buffer);
    }

    return { fullContent, sessionKey, deliveredAt, isDone, aborted: false };
  };

  onStatusChange?.('starting');

  try {
    const streamed = await streamOnce();
    if (streamed.aborted) {
      return {
        fullContent: streamed.fullContent,
        sessionKey: streamed.sessionKey,
        deliveredAt: streamed.deliveredAt,
        recovered: false,
        aborted: true,
      };
    }

    if (streamed.isDone) {
      return {
        fullContent: streamed.fullContent,
        sessionKey: streamed.sessionKey,
        deliveredAt: streamed.deliveredAt,
        recovered: false,
        aborted: false,
        routingSuggestion: undefined,
      };
    }

    throw new Error('Streaming connection ended before completion');
  } catch (streamError) {
    if (signal?.aborted || isAbortError(streamError)) {
      return {
        fullContent: '',
        sessionKey: undefined,
        deliveredAt: undefined,
        recovered: false,
        aborted: true,
        routingSuggestion: undefined,
      };
    }

    console.warn('Streaming interrupted, retrying without stream', streamError);
    onStatusChange?.('recovering');

    const fallback = await requestAgentReply(requestPayload);
    return {
      fullContent: extractAssistantContentFromReply(fallback),
      sessionKey: fallback.sessionKey,
      deliveredAt: fallback.deliveredAt,
      recovered: true,
      aborted: false,
      routingSuggestion: extractRoutingSuggestionFromReply(fallback),
    };
  }
}

async function persistConversation(conversation: Conversation): Promise<Conversation> {
  const response = await authenticatedFetch('/api/conversations', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(conversation),
  });

  const data = await readApiPayload(response);
  if (!response.ok) {
    throw new Error(getApiErrorMessage(data));
  }

  const rawConversation = data && typeof data === 'object'
    ? ('data' in data ? data.data : 'conversation' in data ? data.conversation : data)
    : data;

  return normalizeConversation(rawConversation ?? conversation);
}

async function persistConversationUpdates(
  conversationId: string,
  updates: Partial<Conversation>,
): Promise<Conversation> {
  const response = await authenticatedFetch(`/api/conversations/${conversationId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(updates),
  });

  const data = await readApiPayload(response);
  if (!response.ok) {
    throw new Error(getApiErrorMessage(data));
  }

  const rawConversation = data && typeof data === 'object'
    ? ('data' in data ? data.data : 'conversation' in data ? data.conversation : data)
    : data;

  return normalizeConversation(rawConversation ?? { id: conversationId, ...updates });
}

// D6-12: auto conversation titles. After the opening exchange completes, ask
// the server for a short LLM-generated title (capped D3-16-style transcript on
// the server side) and apply it through the existing rename path — but only
// while the title is still the auto-derived one, so a manual rename always
// wins. Fire-and-forget: title polish must never surface as a chat error.
const autoTitleAttemptedConversations = new Set<string>();

function isAutoDerivedTitle(title: string | undefined, firstUserMessageContent: string | undefined): boolean {
  const currentTitle = String(title || '').trim();
  if (!currentTitle || currentTitle === 'New Chat') {
    return true;
  }
  if (!firstUserMessageContent) {
    return false;
  }
  return currentTitle === generateConversationTitle(firstUserMessageContent);
}

async function maybeAutoTitleConversation({
  get,
  conversationId,
}: {
  get: () => ChatState;
  conversationId: string;
}): Promise<void> {
  const state = get();
  const conversation = state.conversations.find((entry) => entry.id === conversationId);
  if (!conversation || conversation.isGroup) {
    return;
  }
  if (autoTitleAttemptedConversations.has(conversationId)) {
    return;
  }

  const conversationMessages = state.messages[conversationId] || [];
  const firstUserMessage = conversationMessages.find(
    (message) => message.role === 'user' && String(message.content || '').trim(),
  );
  const hasAssistantReply = conversationMessages.some(
    (message) => message.role === 'assistant'
      && message.status !== 'failed'
      && !message.isError
      && String(message.content || '').trim(),
  );
  if (!firstUserMessage || !hasAssistantReply) {
    return;
  }
  if (!isAutoDerivedTitle(conversation.title, firstUserMessage.content)) {
    return;
  }

  autoTitleAttemptedConversations.add(conversationId);

  try {
    const response = await authenticatedFetch(`/api/conversations/${conversationId}/auto-title`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });
    const data = await readApiPayload(response);
    if (!response.ok) {
      return;
    }

    const payload = data && typeof data === 'object' ? (data as Record<string, any>) : {};
    const title = String(payload?.data?.title ?? payload?.title ?? '').trim();
    if (!title) {
      return;
    }

    // Re-check just before applying: the user may have renamed mid-flight.
    const latest = get().conversations.find((entry) => entry.id === conversationId);
    if (!latest || !isAutoDerivedTitle(latest.title, firstUserMessage.content)) {
      return;
    }
    if (String(latest.title || '').trim() === title) {
      return;
    }

    await get().renameConversation(conversationId, title);
  } catch {
    // Cosmetic feature — swallow failures, the derived title stays.
  }
}

async function persistConversationMessage(
  conversationId: string,
  message: Message,
  conversation?: Conversation | null,
): Promise<Message> {
  const response = await authenticatedFetch(`/api/conversations/${conversationId}/messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({
      message,
      conversation,
    }),
  });

  const data = await readApiPayload(response);
  if (!response.ok) {
    throw new Error(getApiErrorMessage(data));
  }

  const rawMessage = data && typeof data === 'object'
    ? ('data' in data ? data.data : 'message' in data ? data.message : data)
    : data;

  return normalizeMessage(rawMessage ?? message, conversationId);
}

async function deletePersistedConversationMessage(conversationId: string, messageId: string): Promise<void> {
  const response = await authenticatedFetch(`/api/conversations/${conversationId}/messages/${messageId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok && response.status !== 404) {
    const data = await readApiPayload(response);
    throw new Error(getApiErrorMessage(data));
  }
}

async function editPersistedConversationMessage(conversationId: string, messageId: string, content: string) {
  const response = await authenticatedFetch(`/api/conversations/${conversationId}/messages/${messageId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ content }),
  });

  const data = await readApiPayload(response);
  if (!response.ok) {
    throw new Error(getApiErrorMessage(data));
  }

  const payload = data && typeof data === 'object' && 'data' in data ? (data as { data?: any }).data : data;
  const rawMessage = payload && typeof payload === 'object' && 'message' in payload ? payload.message : payload;
  const removedMessageIds = payload && typeof payload === 'object' && Array.isArray(payload.removedMessageIds)
    ? payload.removedMessageIds.map((id: unknown) => String(id))
    : Array.isArray((data as any)?.removedMessageIds)
      ? (data as any).removedMessageIds.map((id: unknown) => String(id))
      : [];

  return {
    message: normalizeMessage(rawMessage ?? { id: messageId, conversationId, content }, conversationId),
    removedMessageIds,
  };
}

async function ratePersistedConversationMessage(
  conversationId: string,
  messageId: string,
  rating: 'up' | 'down',
): Promise<Message> {
  const response = await authenticatedFetch(`/api/conversations/${conversationId}/messages/${messageId}/rate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ rating }),
  });

  const data = await readApiPayload(response);
  if (!response.ok) {
    throw new Error(getApiErrorMessage(data));
  }

  const rawMessage = data && typeof data === 'object'
    ? ('data' in data ? (data as { data?: unknown }).data : 'message' in data ? (data as { message?: unknown }).message : data)
    : data;

  return normalizeMessage(rawMessage ?? { id: messageId, conversationId, metadata: { rating } }, conversationId);
}

const noopStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

const persistStorage = createJSONStorage(() => getBrowserStorage() ?? noopStorage);

function fallbackConversation(agentId: string, title?: string, agents: Agent[] = []): Conversation {
  const agent = agents.find((item) => item.id === agentId) ?? DEFAULT_AGENT;
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    title: title || agent.name || 'New Chat',
    agentId,
    agentName: agent.name,
    agentAvatar: agent.avatar,
    type: 'DIRECT',
    kind: '1to1',
    unreadCount: 0,
    createdAt: now,
    updatedAt: now,
    lastActivity: now,
  };
}

function createGroupParticipants(conversationId: string, agentIds: string[], agents: Agent[]): ConversationParticipant[] {
  const now = new Date().toISOString();
  const authState = useAuthStore.getState();
  const currentUser = authState.impersonatedUser ?? authState.user;

  return [
    ...(currentUser
      ? [
          {
            id: crypto.randomUUID(),
            conversationId,
            participantType: 'USER' as const,
            participantId: currentUser.id,
            displayName: currentUser.name,
            role: 'OWNER' as const,
            joinedAt: now,
          },
        ]
      : []),
    ...agentIds.map((agentId) => ({
      id: crypto.randomUUID(),
      conversationId,
      participantType: 'AGENT' as const,
      participantId: agentId,
      displayName: agents.find((agent) => agent.id === agentId)?.name ?? agentId,
      role: 'MEMBER' as const,
      joinedAt: now,
    })),
  ];
}

function isBrowserOffline() {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

async function dispatchUserMessage({
  get,
  set,
  conversationId,
  content,
  attachmentsInput,
  existingMessageId,
  skipOfflineQueue = false,
}: {
  get: () => ChatState;
  set: (partial: any) => void;
  conversationId: string;
  content: string;
  attachmentsInput?: Array<File | Attachment>;
  existingMessageId?: string;
  skipOfflineQueue?: boolean;
}) {
  const trimmed = content.trim();
  if (!trimmed && (!attachmentsInput || attachmentsInput.length === 0)) {
    return;
  }

  const state = get();
  const requestSessionAsyncBoundaryVersion = getSessionAsyncBoundaryVersion();
  const isRequestSessionCurrent = () => isSessionAsyncBoundaryCurrent(requestSessionAsyncBoundaryVersion);
  const conversation = getConversationById(state.conversations, conversationId);
  const existingEntry = existingMessageId ? findMessageById(state.messages, existingMessageId) : null;
  const existingMessage = existingEntry?.message ?? null;
  const authState = useAuthStore.getState();
  const requestOwnerKey = getSessionOwnerKey(authState);
  const currentUser = authState.impersonatedUser ?? authState.user;
  const createdAt = existingMessage?.createdAt ?? new Date().toISOString();
  const fileAttachments = (attachmentsInput ?? []).filter((attachment): attachment is File => attachment instanceof File);
  const plainAttachments = (attachmentsInput ?? [])
    .filter((attachment): attachment is Attachment => !(attachment instanceof File))
    .map((attachment) => normalizeAttachment(attachment));
  const localAttachments = fileAttachments.length > 0
    ? [...fileAttachments.map((file) => makeAttachmentFromFile(file)), ...plainAttachments]
    : plainAttachments;
  const directTargetAgentId = conversation?.isGroup
    ? undefined
    : conversation?.agentId ?? state.agents[0]?.id ?? DEFAULT_AGENT.id;
  const mentionedAgentIds = conversation?.isGroup
    ? extractMentionedAgentIds(trimmed, conversation, state.agents)
    : existingMessage?.mentions ?? [];
  const groupAgentIds = conversation?.isGroup ? getGroupAgentIds(conversation) : [];
  const targetAgentIds = conversation?.isGroup
    ? (mentionedAgentIds.length > 0 ? mentionedAgentIds : groupAgentIds.length === 1 ? groupAgentIds : [])
    : [];
  const fallbackPromptSource = localAttachments.length > 0 ? localAttachments : plainAttachments;
  const fallbackPrompt = fallbackPromptSource.length > 0
    ? `Please review the attached file${fallbackPromptSource.length > 1 ? 's' : ''}: ${fallbackPromptSource.map((attachment) => attachment.filename).join(', ')}`
    : '';
  const prompt = trimmed || fallbackPrompt;
  const messageId = existingMessage?.id ?? crypto.randomUUID();
  let resolvedUserMessageId = messageId;
  const baseUserMessage: Message = {
    ...existingMessage,
    id: messageId,
    conversationId,
    role: 'user',
    content: trimmed,
    attachments: existingMessage?.attachments?.length && fileAttachments.length === 0 ? existingMessage.attachments : localAttachments,
    mentions: mentionedAgentIds,
    targetAgentId: directTargetAgentId,
    createdAt,
    timestamp: existingMessage?.timestamp ?? createdAt,
    sentAt: undefined,
    deliveredAt: undefined,
    status: 'sending',
    error: undefined,
    user: currentUser ? { name: currentUser.name, email: currentUser.email } : existingMessage?.user,
    reactions: existingMessage?.reactions ?? { counts: {}, userReactions: [], reactors: {} },
  };

  if (existingMessage) {
    get().updateMessage(messageId, baseUserMessage);
  } else {
    get().addMessage(baseUserMessage);
  }

  if (!skipOfflineQueue && isBrowserOffline()) {
    const queueItemId = String(existingMessage?.metadata?.offlineQueueId || crypto.randomUUID());
    const queueItem: OfflineQueueItem = {
      id: queueItemId,
      messageId,
      conversationId,
      content: trimmed,
      createdAt,
      attachments: await serializeOfflineAttachments([...(fileAttachments as Array<File | Attachment>), ...plainAttachments]),
      user: currentUser ? { name: currentUser.name, email: currentUser.email } : undefined,
    };

    if (!isRequestSessionCurrent() || !requestOwnerKey) {
      return;
    }

    const offlineQueueItems = upsertOfflineQueueItem(queueItem, requestOwnerKey);
    set((currentState: ChatState) => ({
      isOffline: true,
      offlineQueueItems,
      messages: {
        ...currentState.messages,
        [conversationId]: mergeQueuedMessages(currentState.messages[conversationId] || [], offlineQueueItems, conversationId),
      },
    }));
    get().updateMessage(messageId, {
      status: 'queued',
      error: undefined,
      metadata: {
        ...(baseUserMessage.metadata && typeof baseUserMessage.metadata === 'object' ? baseUserMessage.metadata : {}),
        offlineQueueId: queueItemId,
        queuedAt: createdAt,
      },
    });
    get().setTyping(conversationId, false);
    get().setError(null);
    return;
  }

  get().setTyping(conversationId, true);

  try {
    if (conversation?.isGroup && targetAgentIds.length === 0) {
      throw new Error('Mention one or more agents in this group to route your message.');
    }

    let normalizedAttachments = plainAttachments;
    let requestAttachments = plainAttachments;

    if (fileAttachments.length > 0) {
      pendingUploadFiles.set(messageId, fileAttachments);

      try {
        const uploadedAttachments = await uploadFiles(fileAttachments);
        pendingUploadFiles.delete(messageId);
        if (!isRequestSessionCurrent()) {
          return;
        }
        normalizedAttachments = [...uploadedAttachments, ...plainAttachments];
        requestAttachments = await buildRequestAttachments(uploadedAttachments, fileAttachments, plainAttachments);
        if (!isRequestSessionCurrent()) {
          return;
        }
        get().updateMessage(messageId, {
          attachments: normalizedAttachments,
          error: undefined,
        });
      } catch (error) {
        if (!isRequestSessionCurrent()) {
          return;
        }
        const errorMessage = error instanceof Error ? error.message : 'Failed to upload attachment';
        get().updateMessage(messageId, {
          status: 'failed',
          error: errorMessage,
        });
        get().setError(errorMessage);
        return;
      }
    } else {
      pendingUploadFiles.delete(messageId);
    }

    const sentAt = new Date().toISOString();
    const persistedUserMessage = await persistConversationMessage(
      conversationId,
      {
        ...baseUserMessage,
        attachments: normalizedAttachments,
        mentions: mentionedAgentIds,
        targetAgentId: directTargetAgentId,
        status: 'sent',
        sentAt,
        error: undefined,
      },
      conversation,
    );

    if (!isRequestSessionCurrent()) {
      return;
    }

    get().updateMessage(messageId, {
      ...persistedUserMessage,
      attachments: normalizedAttachments,
      mentions: mentionedAgentIds,
      targetAgentId: directTargetAgentId,
      status: 'sent',
      sentAt: persistedUserMessage.sentAt ?? sentAt,
      error: undefined,
    });

    resolvedUserMessageId = persistedUserMessage.id || messageId;

    const currentConversationMessages = get().messages[conversationId] || [];
    const currentMessageIndex = currentConversationMessages.findIndex((message) => message.id === resolvedUserMessageId);
    const historyMessages = currentMessageIndex >= 0
      ? currentConversationMessages.slice(0, currentMessageIndex)
      : currentConversationMessages.filter((message) => message.id !== resolvedUserMessageId);
    const history = buildHistory(historyMessages);
    const userMessageForRequest: Message = {
      ...baseUserMessage,
      id: resolvedUserMessageId,
      attachments: normalizedAttachments,
      mentions: mentionedAgentIds,
      targetAgentId: directTargetAgentId,
      status: 'sent',
      sentAt: persistedUserMessage.sentAt ?? sentAt,
      error: undefined,
    };

    const markDelivered = (deliveredAt?: string) => {
      if (!isRequestSessionCurrent()) {
        return;
      }

      get().updateMessage(resolvedUserMessageId, {
        status: 'delivered',
        deliveredAt: deliveredAt || new Date().toISOString(),
        error: undefined,
      });
    };

    if (conversation?.isGroup) {
      let deliveredToAnyAgent = false;
      let assistantSucceeded = false;
      let lastAgentError = '';
      let stoppedByUser = false;

      for (const agentId of targetAgentIds) {
        if (!isRequestSessionCurrent()) {
          stoppedByUser = true;
          break;
        }

        const assistantMessageId = crypto.randomUUID();
        const assistantCreatedAt = new Date().toISOString();
        const streamController = new AbortController();
        let streamedContent = '';

        registerStreamController(conversationId, assistantMessageId, streamController);
        get().addMessage({
          id: assistantMessageId,
          conversationId,
          role: 'assistant',
          content: '',
          createdAt: assistantCreatedAt,
          timestamp: assistantCreatedAt,
          status: 'streaming',
          agentId,
          metadata: { streamingState: 'starting', connectionDrops: 0 },
          reactions: { counts: {}, userReactions: [], reactors: {} },
        });
        get().setTyping(conversationId, false);

        try {
          const data = await requestAgentReplyStream({
            message: prompt,
            agentId,
            conversationId,
            history,
            messageId: resolvedUserMessageId,
            attachments: requestAttachments,
            userMessage: userMessageForRequest,
            assistantMessageId,
            conversation,
            signal: streamController.signal,
            onDelivered: (deliveredAt) => {
              if (!isRequestSessionCurrent()) {
                return;
              }

              deliveredToAnyAgent = true;
              markDelivered(deliveredAt);
            },
            onDelta: (delta) => {
              if (!isRequestSessionCurrent()) {
                return;
              }

              streamedContent += delta;
              get().updateMessage(assistantMessageId, {
                content: streamedContent,
                metadata: { streamingState: 'streaming' },
              });
            },
            onStatusChange: (streamingState) => {
              if (!isRequestSessionCurrent()) {
                return;
              }

              get().updateMessage(assistantMessageId, {
                metadata: {
                  streamingState,
                  connectionDrops: streamingState === 'recovering' ? 1 : 0,
                },
              });
            },
          });

          if (!isRequestSessionCurrent()) {
            stoppedByUser = true;
            break;
          }

          if (data.deliveredAt) {
            deliveredToAnyAgent = true;
            markDelivered(data.deliveredAt);
          }

          if (data.sessionKey) {
            get().updateConversation(conversationId, { sessionKey: String(data.sessionKey) });
          }

          if (data.aborted) {
            stoppedByUser = true;
            const partialContent = data.fullContent || streamedContent;
            if (partialContent) {
              get().updateMessage(assistantMessageId, {
                content: partialContent,
                status: 'sent',
                metadata: { streamingState: 'stopped', connectionDrops: 0 },
              });
            } else {
              get().deleteMessage(assistantMessageId);
            }
            break;
          }

          const finalContent = data.fullContent || streamedContent || 'No response';
          get().updateMessage(assistantMessageId, {
            content: finalContent,
            status: 'sent',
            metadata: {
              ...(data.recovered ? { streamingState: 'recovered', connectionDrops: 1 } : {}),
              ...(data.routingSuggestion ? { routingSuggestion: data.routingSuggestion } : {}),
            },
          });
          assistantSucceeded = true;
        } catch (error) {
          if (!isRequestSessionCurrent()) {
            stoppedByUser = true;
            break;
          }

          lastAgentError = error instanceof Error ? error.message : 'Failed to get a response';
          get().updateMessage(assistantMessageId, {
            content: streamedContent || lastAgentError,
            status: 'failed',
            isError: true,
            error: lastAgentError,
            metadata: { streamingState: 'failed', connectionDrops: 1 },
          });
        } finally {
          unregisterStreamController(conversationId, assistantMessageId);
        }
      }

      if (!stoppedByUser) {
        if (!deliveredToAnyAgent && !assistantSucceeded) {
          throw new Error(lastAgentError || 'Failed to send message');
        }
        void get().loadMessages(conversationId);
      }
    } else {
      const agentId = directTargetAgentId ?? DEFAULT_AGENT.id;
      const assistantMessageId = crypto.randomUUID();
      const assistantCreatedAt = new Date().toISOString();

      try {
        const data = await requestAgentReplyWithJob({
          message: prompt,
          agentId,
          conversationId,
          history,
          messageId: resolvedUserMessageId,
          attachments: requestAttachments,
          userMessage: userMessageForRequest,
          assistantMessageId,
          conversation,
        });

        if (!isRequestSessionCurrent()) {
          return;
        }

        if (isAsyncJobProcessingResponse(data)) {
          upsertLocalConversationMessage(set, conversationId, {
            id: assistantMessageId,
            conversationId,
            role: 'assistant',
            content: '',
            createdAt: assistantCreatedAt,
            timestamp: assistantCreatedAt,
            status: 'pending',
            agentId,
            metadata: {
              asyncJob: {
                jobId: String(data.jobId),
                status: 'processing',
                progressMessage: typeof data.progressMessage === 'string' && data.progressMessage.trim()
                  ? data.progressMessage
                  : 'Thinking...',
                startedAt: typeof data.startedAt === 'string' && data.startedAt.trim()
                  ? data.startedAt
                  : assistantCreatedAt,
                elapsedMs: Math.max(0, Number(data.elapsedMs || 0) || 0),
                userMessageId: resolvedUserMessageId,
                assistantMessageId,
              } as AsyncJobMetadata,
            },
            reactions: { counts: {}, userReactions: [], reactors: {} },
          });
          startPendingJobPolling({
            get,
            set,
            jobId: String(data.jobId),
            assistantMessageId,
            conversationId,
            userMessageId: resolvedUserMessageId,
          });
          get().updateMessage(resolvedUserMessageId, {
            status: typeof data.userMessageStatus === 'string' ? data.userMessageStatus as Message['status'] : 'sent',
            error: undefined,
          });
          get().setTyping(conversationId, false);
          get().setError(null);
          return;
        }

        const finalContent = extractAssistantContentFromReply(data) || 'No response';
        markDelivered(typeof data.deliveredAt === 'string' ? data.deliveredAt : undefined);

        if (data.sessionKey) {
          get().updateConversation(conversationId, { sessionKey: String(data.sessionKey) });
        }

        get().addMessage({
          id: assistantMessageId,
          conversationId,
          role: 'assistant',
          content: finalContent,
          createdAt: assistantCreatedAt,
          timestamp: assistantCreatedAt,
          status: 'sent',
          agentId,
          metadata: {
            ...(data.routingSuggestion ? { routingSuggestion: data.routingSuggestion } : {}),
          },
          suggestions: Array.isArray(data.suggestions) ? data.suggestions : [],
          reactions: { counts: {}, userReactions: [], reactors: {} },
        });
        void get().loadMessages(conversationId);
        // D6-12: opening exchange just completed — try an LLM title.
        void maybeAutoTitleConversation({ get, conversationId });
      } catch (error) {
        throw error;
      }
    }

    get().setError(null);
  } catch (error) {
    if (!isRequestSessionCurrent()) {
      return;
    }

    const errorMessage = error instanceof Error ? error.message : 'Failed to send message';
    const currentStatus = findMessageById(get().messages, resolvedUserMessageId)?.message.status;
    if (currentStatus !== 'delivered') {
      get().updateMessage(resolvedUserMessageId, {
        status: 'failed',
        error: errorMessage,
      });
    }
    get().setError(errorMessage);
  } finally {
    if (isRequestSessionCurrent()) {
      get().setTyping(conversationId, false);
    }
  }
}

export const useChatStore = create<ChatState>()(
  persist(
    subscribeWithSelector((set, get) => ({
      conversations: [],
      activeConversationId: null,
      messages: {},
      messagePaging: {},
      loadingOlderMessages: {},
      asyncJobSummaries: {},
      streamingTokens: {},
      streamingMessageIds: {},
      isTyping: false,
      typing: {},
      agents: [],
      agentStatuses: {},
      composerDrafts: {},
      suggestionDispatchVersion: {},
      persistedSessionOwnerKey: null,
      isLoading: false,
      isLoadingAgents: false,
      isLoadingConversations: false,
      isLoadingTags: false,
      isSearchingConversations: false,
      error: null,
      searchError: null,
      voiceEnabled: false,
      sidebarConversationsEnabled: getStoredSidebarConversationsEnabled(),
      favoriteAgentIds: getStoredFavoriteAgentIds(),
      tags: [],
      activeTagFilterIds: [],
      tagMatchMode: 'or',
      showArchived: false,
      showHidden: false,
      searchResults: [],
      searchTotal: 0,
      searchHasMore: false,
      isOffline: isBrowserOffline(),
      offlineQueueItems: [],
      isFlushingOfflineQueue: false,

      initializeOfflineQueue: () => {
        const requestBoundary = captureOwnerScopedRequestBoundary();
        if (!requestBoundary.ownerKey) {
          set({ isOffline: isBrowserOffline(), offlineQueueItems: [] });
          return;
        }

        const offlineQueueItems = loadOfflineQueue(requestBoundary.ownerKey);
        if (!requestBoundary.isCurrent()) {
          return;
        }

        set((state) => {
          const nextMessages = { ...state.messages };

          offlineQueueItems.forEach((item) => {
            nextMessages[item.conversationId] = mergeQueuedMessages(nextMessages[item.conversationId] || [], offlineQueueItems, item.conversationId);
          });

          return {
            isOffline: isBrowserOffline(),
            offlineQueueItems,
            messages: nextMessages,
          };
        });
      },

      setOfflineStatus: (isOffline: boolean) => {
        set({ isOffline });
      },

      flushOfflineQueue: async () => {
        const requestBoundary = captureOwnerScopedRequestBoundary();
        if (!requestBoundary.ownerKey) {
          set({ isOffline: isBrowserOffline(), isFlushingOfflineQueue: false, offlineQueueItems: [] });
          return;
        }

        if (isBrowserOffline()) {
          set({ isOffline: true, isFlushingOfflineQueue: false });
          return;
        }

        const queueSnapshot = [...get().offlineQueueItems].sort((left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime());
        if (queueSnapshot.length === 0) {
          set({ isOffline: false, isFlushingOfflineQueue: false });
          return;
        }

        set({ isOffline: false, isFlushingOfflineQueue: true });

        for (const item of queueSnapshot) {
          if (!requestBoundary.isCurrent()) {
            return;
          }

          try {
            await dispatchUserMessage({
              get,
              set,
              conversationId: item.conversationId,
              content: item.content,
              attachmentsInput: deserializeOfflineAttachments(item.attachments),
              existingMessageId: item.messageId,
              skipOfflineQueue: true,
            });

            if (!requestBoundary.isCurrent()) {
              return;
            }

            const offlineQueueItems = removeOfflineQueueItem(item.id, requestBoundary.ownerKey);
            if (!requestBoundary.isCurrent()) {
              return;
            }

            set((state) => ({
              offlineQueueItems,
              messages: {
                ...state.messages,
                [item.conversationId]: mergeQueuedMessages(
                  (state.messages[item.conversationId] || []).map((message) => (
                    message.id === item.messageId && message.status === 'queued'
                      ? { ...message, status: 'sending' }
                      : message
                  )),
                  offlineQueueItems,
                  item.conversationId,
                ),
              },
            }));
          } catch (error) {
            if (!requestBoundary.isCurrent()) {
              return;
            }

            if (isBrowserOffline()) {
              set({ isOffline: true, isFlushingOfflineQueue: false });
              return;
            }

            console.error('Failed to flush offline queue item:', error);
            break;
          }
        }

        if (!requestBoundary.isCurrent()) {
          return;
        }

        set({ isOffline: isBrowserOffline(), isFlushingOfflineQueue: false, offlineQueueItems: loadOfflineQueue(requestBoundary.ownerKey) });
      },

      setActiveConversation: (conversationId: string | null) => {
        set({ activeConversationId: conversationId });

        if (conversationId) {
          void get().loadMessages(conversationId);
          void fetch(`/api/conversations/${conversationId}/read`, {
            method: 'POST',
            headers: getAuthHeaders(),
          }).catch(() => undefined);

          set((state) => ({
            conversations: state.conversations.map((conversation) =>
              conversation.id === conversationId ? { ...conversation, unreadCount: 0, isUnread: false } : conversation,
            ),
            typing: {
              ...state.typing,
              [conversationId]: state.typing[conversationId] ?? false,
            },
          }));
        }
      },

      loadConversations: async () => {
        const requestBoundary = captureOwnerScopedRequestBoundary();
        set({ isLoading: true, isLoadingConversations: true, error: null });

        if (!requestBoundary.ownerKey) {
          set({ isLoading: false, isLoadingConversations: false });
          return;
        }

        try {
          const state = get();
          const params = new URLSearchParams();
          const viewAsKey = useAuthStore.getState().viewAsUser?.email || useAuthStore.getState().viewAsUser?.id || '';
          if (viewAsKey) {
            params.set('viewAs', viewAsKey);
          }
          if (state.showArchived) {
            params.set('archived', 'only');
          }
          if (state.showHidden) {
            params.set('includeHidden', 'true');
          }
          if (state.activeTagFilterIds.length > 0) {
            params.set('tagIds', state.activeTagFilterIds.join(','));
            params.set('tagMatch', state.tagMatchMode);
          }

          const response = await authenticatedFetch(`/api/conversations${params.toString() ? `?${params.toString()}` : ''}`, {
            headers: getAuthHeaders(),
          });

          if (!requestBoundary.isCurrent()) {
            return;
          }

          if (!response.ok) {
            throw new Error('Failed to load conversations');
          }

          const data = await response.json();
          if (!requestBoundary.isCurrent()) {
            return;
          }

          const rawConversations = Array.isArray(data)
            ? data
            : Array.isArray(data?.conversations)
              ? data.conversations
              : [];
          const conversations: Conversation[] = syncConversationTags(rawConversations.map(normalizeConversation), get().tags);
          const existingActiveConversationId = get().activeConversationId;
          const nextActiveConversationId = existingActiveConversationId && conversations.some((conversation) => conversation.id === existingActiveConversationId)
            ? existingActiveConversationId
            : conversations[0]?.id ?? (state.showArchived ? null : existingActiveConversationId) ?? null;

          set({
            conversations,
            activeConversationId: nextActiveConversationId,
            isLoading: false,
            isLoadingConversations: false,
            error: null,
          });

          if (!requestBoundary.isCurrent()) {
            return;
          }

          get().resumePendingJobs();

          if (nextActiveConversationId) {
            void get().loadMessages(nextActiveConversationId);
          }
        } catch (error) {
          if (!requestBoundary.isCurrent()) {
            return;
          }

          set({
            isLoading: false,
            isLoadingConversations: false,
            error: error instanceof Error ? error.message : 'Failed to load conversations',
          });
        }
      },

      loadMessages: async (conversationId: string) => {
        const requestSessionAsyncBoundaryVersion = getSessionAsyncBoundaryVersion();
        const isRequestSessionCurrent = () => isSessionAsyncBoundaryCurrent(requestSessionAsyncBoundaryVersion);

        set((state) => ({
          messages: {
            ...state.messages,
            [conversationId]: state.messages[conversationId] ?? [],
          },
        }));

        try {
          const previousMessages = get().messages[conversationId] ?? [];
          const previousIds = new Set(previousMessages.map((message) => message.id));
          const viewAsKeyForMessages = useAuthStore.getState().viewAsUser?.email || useAuthStore.getState().viewAsUser?.id || '';
          const response = await authenticatedFetch(`/api/conversations/${conversationId}/messages${viewAsKeyForMessages ? `?viewAs=${encodeURIComponent(viewAsKeyForMessages)}` : ''}`, {
            headers: getAuthHeaders(),
          });

          if (!response.ok) {
            throw new Error('Failed to load messages');
          }

          const data = await response.json();
          const rawMessages = Array.isArray(data)
            ? data
            : Array.isArray(data?.messages)
              ? data.messages
              : [];
          if (!isRequestSessionCurrent()) {
            return;
          }

          const normalizedMessages = sortMessages(rawMessages.map((message: any) => normalizeMessage(message, conversationId)));
          const mergedMessages = mergePendingAsyncJobPlaceholders(
            mergeQueuedMessages(normalizedMessages, get().offlineQueueItems, conversationId),
            previousMessages,
          );

          const paging = data?.paging && typeof data.paging === 'object'
            ? {
                hasMore: Boolean(data.paging.hasMore),
                nextBefore: data.paging.nextBefore ? String(data.paging.nextBefore) : null,
                total: Number(data.paging.total) || mergedMessages.length,
              }
            : { hasMore: false, nextBefore: null, total: mergedMessages.length };

          set((state) => ({
            messages: {
              ...state.messages,
              [conversationId]: mergedMessages,
            },
            messagePaging: {
              ...state.messagePaging,
              [conversationId]: paging,
            },
            error: null,
          }));

          const lastMessage = mergedMessages[mergedMessages.length - 1] ?? null;
          if (lastMessage) {
            get().updateConversationLastMessage(lastMessage);
          }

          mergedMessages
            .filter((message) => message.role === 'assistant' && !previousIds.has(message.id))
            .forEach((message) => {
              maybeNotifyAssistantMessage(message, get().conversations, get().agents, get().activeConversationId);
            });

          get().resumePendingJobs();
        } catch (error) {
          if (!isRequestSessionCurrent()) {
            return;
          }

          if ((get().messages[conversationId] ?? []).length === 0) {
            set({
              error: error instanceof Error ? error.message : 'Failed to load messages',
            });
          }
        }
      },

      // Wave 4: fetch the next-older history page via the ?before cursor and
      // prepend it (deduped). Returns true when a page was loaded.
      loadOlderMessages: async (conversationId: string) => {
        const paging = get().messagePaging[conversationId];
        if (!paging?.hasMore || !paging.nextBefore || get().loadingOlderMessages[conversationId]) {
          return false;
        }

        set((state) => ({
          loadingOlderMessages: { ...state.loadingOlderMessages, [conversationId]: true },
        }));

        try {
          const viewAsKey = useAuthStore.getState().viewAsUser?.email || useAuthStore.getState().viewAsUser?.id || '';
          const params = new URLSearchParams({ before: paging.nextBefore });
          if (viewAsKey) {
            params.set('viewAs', viewAsKey);
          }
          const response = await authenticatedFetch(`/api/conversations/${conversationId}/messages?${params.toString()}`, {
            headers: getAuthHeaders(),
          });

          if (!response.ok) {
            throw new Error('Failed to load older messages');
          }

          const data = await response.json();
          const rawMessages = Array.isArray(data?.messages) ? data.messages : Array.isArray(data?.data) ? data.data : [];
          const olderMessages = sortMessages(rawMessages.map((message: any) => normalizeMessage(message, conversationId)));

          set((state) => {
            const existing = state.messages[conversationId] ?? [];
            const existingIds = new Set(existing.map((message) => message.id));
            const newOlder = olderMessages.filter((message) => !existingIds.has(message.id));
            return {
              messages: {
                ...state.messages,
                [conversationId]: [...newOlder, ...existing],
              },
              messagePaging: {
                ...state.messagePaging,
                [conversationId]: {
                  hasMore: Boolean(data?.paging?.hasMore),
                  nextBefore: data?.paging?.nextBefore ? String(data.paging.nextBefore) : null,
                  total: Number(data?.paging?.total) || (state.messagePaging[conversationId]?.total ?? 0),
                },
              },
            };
          });

          return olderMessages.length > 0;
        } catch (error) {
          console.warn('Failed to load older messages:', error);
          return false;
        } finally {
          set((state) => ({
            loadingOlderMessages: { ...state.loadingOlderMessages, [conversationId]: false },
          }));
        }
      },

      loadAgents: async () => {
        const requestBoundary = captureOwnerScopedRequestBoundary();
        set({ isLoadingAgents: true, error: null });

        if (!requestBoundary.ownerKey) {
          set({ isLoadingAgents: false });
          return;
        }

        try {
          let agents: Agent[] = [];

          try {
            const response = await authenticatedFetch('/api/agents', {
              headers: getAuthHeaders(),
            });

            if (!requestBoundary.isCurrent()) {
              return;
            }

            if (response.ok) {
              const data = await response.json();
              if (!requestBoundary.isCurrent()) {
                return;
              }

              const rawAgents = Array.isArray(data) ? data : Array.isArray(data?.agents) ? data.agents : [];
              agents = rawAgents.map(normalizeAgent);
            }
          } catch (error) {
            if (!requestBoundary.isCurrent()) {
              return;
            }

            console.warn('Falling back to /api/config for agents:', error);
          }

          if (!requestBoundary.isCurrent()) {
            return;
          }

          if (agents.length === 0) {
            const response = await authenticatedFetch('/api/config', {
              headers: getAuthHeaders(),
            });

            if (!requestBoundary.isCurrent()) {
              return;
            }

            if (!response.ok) {
              throw new Error('Failed to load agents');
            }

            const data = await response.json();
            if (!requestBoundary.isCurrent()) {
              return;
            }

            const rawAgents = Array.isArray(data?.agents) ? data.agents : [];
            agents = rawAgents.map(normalizeAgent);
          }

          if (!requestBoundary.isCurrent()) {
            return;
          }

          if (agents.length > 0) {
            get().setAgents(agents);
          } else if (get().agents.length === 0) {
            get().setAgents([DEFAULT_AGENT]);
          }

          await get().loadUserPreferences();
          if (!requestBoundary.isCurrent()) {
            return;
          }

          await get().fetchAgentStatuses().catch((statusError) => {
            if (requestBoundary.isCurrent()) {
              console.warn('Failed to refresh agent statuses:', statusError);
            }
          });
          if (!requestBoundary.isCurrent()) {
            return;
          }

          set({ isLoadingAgents: false, error: null });
        } catch (error) {
          if (!requestBoundary.isCurrent()) {
            return;
          }

          if (get().agents.length === 0) {
            get().setAgents([DEFAULT_AGENT]);
          }
          set({
            isLoadingAgents: false,
            error: error instanceof Error ? error.message : 'Failed to load agents',
          });
        }
      },

      fetchAgentStatuses: async () => {
        const requestBoundary = captureOwnerScopedRequestBoundary();
        if (!requestBoundary.ownerKey) {
          return;
        }

        try {
          const response = await authenticatedFetch('/api/agents/status', {
            headers: getAuthHeaders(),
          });
          if (!requestBoundary.isCurrent()) {
            return;
          }

          const data = await readApiPayload(response);
          if (!requestBoundary.isCurrent()) {
            return;
          }

          if (!response.ok) {
            throw new Error(getApiErrorMessage(data));
          }

          const payload = data && typeof data === 'object' && 'data' in data
            ? (data as { data?: Record<string, unknown> }).data
            : data;
          const statusEntries = payload && typeof payload === 'object' ? Object.entries(payload) : [];
          const nextStatuses: Record<string, AgentRuntimeStatus> = {};

          for (const [agentId, rawStatus] of statusEntries) {
            if (!rawStatus || typeof rawStatus !== 'object') {
              continue;
            }

            const normalizedStatus = String((rawStatus as any).status || '').toLowerCase();
            const status: AgentRuntimeStatus['status'] = normalizedStatus === 'degraded'
              ? 'degraded'
              : normalizedStatus === 'slow'
                ? 'slow'
                : ['online', 'healthy'].includes(normalizedStatus)
                  ? 'online'
                  : 'offline';

            nextStatuses[String(agentId)] = {
              status,
              lastCheck: typeof (rawStatus as any).lastCheck === 'string' ? (rawStatus as any).lastCheck : undefined,
              responseTimeMs: typeof (rawStatus as any).responseTimeMs === 'number' ? (rawStatus as any).responseTimeMs : null,
            };
          }

          if (!requestBoundary.isCurrent() || areAgentStatusesEqual(get().agentStatuses, nextStatuses)) {
            return;
          }

          set({
            agentStatuses: nextStatuses,
          });
        } catch (error) {
          if (requestBoundary.isCurrent()) {
            console.warn('Failed to load agent statuses:', error);
          }
        }
      },

      loadUserPreferences: async () => {
        const requestBoundary = captureOwnerScopedRequestBoundary();
        if (!requestBoundary.ownerKey) {
          return;
        }

        try {
          const response = await authenticatedFetch('/api/user/preferences', {
            headers: getAuthHeaders(),
          });
          if (!requestBoundary.isCurrent()) {
            return;
          }

          const data = await readApiPayload(response);
          if (!requestBoundary.isCurrent()) {
            return;
          }

          if (!response.ok) {
            throw new Error(getApiErrorMessage(data));
          }

          const preferences = data && typeof data === 'object' && 'data' in data
            ? (data as { data?: { favoriteAgentIds?: unknown } }).data
            : null;

          if (requestBoundary.isCurrent() && preferences && Array.isArray(preferences.favoriteAgentIds)) {
            get().setFavoriteAgentIds(normalizeFavoriteAgentIds(preferences.favoriteAgentIds));
          }
        } catch (error) {
          if (requestBoundary.isCurrent()) {
            console.warn('Failed to load user preferences:', error);
          }
        }
      },

      loadTags: async () => {
        const requestBoundary = captureOwnerScopedRequestBoundary();
        set({ isLoadingTags: true, error: null });

        if (!requestBoundary.ownerKey) {
          set({ isLoadingTags: false });
          return;
        }

        try {
          const response = await authenticatedFetch('/api/tags', {
            headers: getAuthHeaders(),
          });
          if (!requestBoundary.isCurrent()) {
            return;
          }

          const data = await readApiPayload(response);
          if (!requestBoundary.isCurrent()) {
            return;
          }

          if (!response.ok) {
            throw new Error(getApiErrorMessage(data));
          }

          const rawTags = data && typeof data === 'object'
            ? Array.isArray((data as { tags?: unknown[] }).tags)
              ? (data as { tags: unknown[] }).tags
              : Array.isArray((data as { data?: unknown[] }).data)
                ? (data as { data: unknown[] }).data
                : []
            : [];
          const tags = rawTags.map((tag) => normalizeTag(tag));

          set((state) => ({
            tags,
            conversations: syncConversationTags(state.conversations, tags),
            isLoadingTags: false,
            error: null,
          }));
        } catch (error) {
          if (!requestBoundary.isCurrent()) {
            return;
          }

          set({
            isLoadingTags: false,
            error: error instanceof Error ? error.message : 'Failed to load tags',
          });
        }
      },

      setAgents: (agents: Agent[]) => {
        set((state) => {
          const normalized = agents.map((agent) => {
            const existing = state.agents.find((item) => item.id === agent.id);
            return normalizeAgent({
              ...existing,
              ...agent,
              type: agent.type ?? existing?.type ?? DEFAULT_AGENT.type,
            });
          });

          return {
            agents: normalized,
            conversations: state.conversations.map((conversation) => {
              const agent = normalized.find((item) => item.id === conversation.agentId);
              return agent
                ? { ...conversation, agentName: agent.name, agentAvatar: agent.avatar }
                : conversation;
            }),
          };
        });
      },

      setFavoriteAgentIds: (agentIds) => {
        const normalizedAgentIds = normalizeFavoriteAgentIds(agentIds);
        setStoredFavoriteAgentIds(normalizedAgentIds);
        set({ favoriteAgentIds: normalizedAgentIds });
      },

      ensureDefaultConversation: (agentId: string) => {
        const state = get();
        const existingConversation = state.conversations.find(
          (conversation) => !conversation.isGroup && conversation.agentId === agentId,
        );

        if (existingConversation) {
          if (!state.activeConversationId) {
            set({ activeConversationId: existingConversation.id });
          }
          return;
        }

        const conversation = fallbackConversation(agentId, undefined, state.agents);
        set((currentState) => ({
          conversations: [conversation, ...currentState.conversations],
          activeConversationId: currentState.activeConversationId ?? conversation.id,
          messages: {
            ...currentState.messages,
            [conversation.id]: currentState.messages[conversation.id] ?? [],
          },
        }));
      },

      createTag: async (input) => {
        const tempTag = normalizeTag({
          id: `temp-${crypto.randomUUID()}`,
          name: input.name,
          color: input.color,
        });
        set((state) => ({ tags: [...state.tags, tempTag].sort((left, right) => left.name.localeCompare(right.name)) }));

        try {
          const response = await authenticatedFetch('/api/tags', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders(),
            },
            body: JSON.stringify(input),
          });
          const data = await readApiPayload(response);
          if (!response.ok) {
            throw new Error(getApiErrorMessage(data));
          }

          const storedTag = normalizeTagApiPayload(data, tempTag);
          set((state) => ({
            tags: state.tags
              .map((tag) => tag.id === tempTag.id ? storedTag : tag)
              .sort((left, right) => left.name.localeCompare(right.name)),
            conversations: state.conversations.map((conversation) => ({
              ...conversation,
              tags: (conversation.tags ?? []).map((tag) => tag.id === tempTag.id ? storedTag : tag),
            })),
            error: null,
          }));
          return storedTag;
        } catch (error) {
          set((state) => ({
            tags: state.tags.filter((tag) => tag.id !== tempTag.id),
            error: error instanceof Error ? error.message : 'Failed to create tag',
          }));
          return null;
        }
      },

      updateTag: async (tagId, updates) => {
        const previousTag = get().tags.find((tag) => tag.id === tagId) ?? null;
        if (!previousTag) {
          return null;
        }

        const optimisticTag = normalizeTag({ ...previousTag, ...updates, id: tagId });
        set((state) => ({
          tags: state.tags
            .map((tag) => tag.id === tagId ? optimisticTag : tag)
            .sort((left, right) => left.name.localeCompare(right.name)),
          conversations: state.conversations.map((conversation) => ({
            ...conversation,
            tags: (conversation.tags ?? []).map((tag) => tag.id === tagId ? optimisticTag : tag),
          })),
        }));

        try {
          const response = await authenticatedFetch(`/api/tags/${tagId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders(),
            },
            body: JSON.stringify(updates),
          });
          const data = await readApiPayload(response);
          if (!response.ok) {
            throw new Error(getApiErrorMessage(data));
          }

          const storedTag = normalizeTagApiPayload(data, optimisticTag);
          set((state) => ({
            tags: state.tags
              .map((tag) => tag.id === tagId ? storedTag : tag)
              .sort((left, right) => left.name.localeCompare(right.name)),
            conversations: state.conversations.map((conversation) => ({
              ...conversation,
              tags: (conversation.tags ?? []).map((tag) => tag.id === tagId ? storedTag : tag),
            })),
            error: null,
          }));
          return storedTag;
        } catch (error) {
          set((state) => ({
            tags: state.tags
              .map((tag) => tag.id === tagId ? previousTag : tag)
              .sort((left, right) => left.name.localeCompare(right.name)),
            conversations: state.conversations.map((conversation) => ({
              ...conversation,
              tags: (conversation.tags ?? []).map((tag) => tag.id === tagId ? previousTag : tag),
            })),
            error: error instanceof Error ? error.message : 'Failed to update tag',
          }));
          return null;
        }
      },

      deleteTag: async (tagId) => {
        const previousTags = get().tags;
        const previousConversations = get().conversations;
        set((state) => ({
          tags: state.tags.filter((tag) => tag.id !== tagId),
          conversations: state.conversations.map((conversation) => ({
            ...conversation,
            tags: (conversation.tags ?? []).filter((tag) => tag.id !== tagId),
          })),
        }));

        try {
          const response = await authenticatedFetch(`/api/tags/${tagId}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
          });
          const data = await readApiPayload(response);
          if (!response.ok) {
            throw new Error(getApiErrorMessage(data));
          }
          set({ error: null });
        } catch (error) {
          set({
            tags: previousTags,
            conversations: previousConversations,
            error: error instanceof Error ? error.message : 'Failed to delete tag',
          });
          throw error;
        }
      },

      setConversationTags: async (conversationId, tagIds) => {
        const state = get();
        const existingConversation = state.conversations.find((conversation) => conversation.id === conversationId) ?? null;
        if (!existingConversation) {
          return;
        }

        const previousConversation = existingConversation;
        const normalizedTagIds = [...new Set(tagIds.map((tagId) => String(tagId).trim()).filter(Boolean))];
        const optimisticTags = buildConversationTags(normalizedTagIds, state.tags);

        set((currentState) => ({
          conversations: currentState.conversations.map((conversation) =>
            conversation.id === conversationId ? { ...conversation, tags: optimisticTags } : conversation,
          ),
        }));

        try {
          const response = await authenticatedFetch(`/api/conversations/${conversationId}/tags`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders(),
            },
            body: JSON.stringify({ tagIds: normalizedTagIds }),
          });
          const data = await readApiPayload(response);
          if (!response.ok) {
            throw new Error(getApiErrorMessage(data));
          }

          const storedConversation = normalizeConversationApiPayload(data, { ...existingConversation, tags: optimisticTags });
          set((currentState) => ({
            conversations: currentState.conversations.map((conversation) =>
              conversation.id === conversationId ? storedConversation : conversation,
            ),
            error: null,
          }));
          void get().loadConversations();
        } catch (error) {
          set((currentState) => ({
            conversations: currentState.conversations.map((conversation) =>
              conversation.id === conversationId ? previousConversation : conversation,
            ),
            error: error instanceof Error ? error.message : 'Failed to update conversation tags',
          }));
          throw error;
        }
      },

      toggleConversationPinned: async (conversationId) => {
        const state = get();
        const existingConversation = state.conversations.find((conversation) => conversation.id === conversationId) ?? null;
        if (!existingConversation) {
          return;
        }

        const previousConversation = existingConversation;
        const wasPinned = existingConversation.pinOrder !== null && existingConversation.pinOrder !== undefined
          ? true
          : Boolean(existingConversation.isPinned);
        const pinnedOrders = state.conversations
          .filter((conversation) => conversation.id !== conversationId && conversation.pinOrder !== null && conversation.pinOrder !== undefined)
          .map((conversation) => Number(conversation.pinOrder ?? 0));
        const nextPinOrder = pinnedOrders.length > 0 ? Math.max(...pinnedOrders) + 1 : 0;
        const optimisticConversation: Conversation = {
          ...existingConversation,
          isPinned: wasPinned ? false : true,
          pinOrder: wasPinned ? null : nextPinOrder,
        };

        set((currentState) => ({
          conversations: currentState.conversations.map((conversation) =>
            conversation.id === conversationId ? optimisticConversation : conversation,
          ),
        }));

        try {
          const response = await authenticatedFetch(`/api/conversations/${conversationId}/pin`, {
            method: wasPinned ? 'DELETE' : 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders(),
            },
          });
          const data = await readApiPayload(response);
          if (!response.ok) {
            throw new Error(getApiErrorMessage(data));
          }

          const storedConversation = normalizeConversationApiPayload(data, optimisticConversation);
          set((currentState) => ({
            conversations: currentState.conversations.map((conversation) =>
              conversation.id === conversationId ? storedConversation : conversation,
            ),
            error: null,
          }));
          void get().loadConversations();
        } catch (error) {
          set((currentState) => ({
            conversations: currentState.conversations.map((conversation) =>
              conversation.id === conversationId ? previousConversation : conversation,
            ),
            error: error instanceof Error ? error.message : 'Failed to update pinned conversation',
          }));
        }
      },

      toggleConversationArchived: async (conversationId) => {
        const state = get();
        const existingConversation = state.conversations.find((conversation) => conversation.id === conversationId) ?? null;
        if (!existingConversation) {
          return;
        }

        const previousConversation = existingConversation;
        const nextArchivedAt = existingConversation.archivedAt ? null : new Date().toISOString();
        const optimisticConversation: Conversation = {
          ...existingConversation,
          archivedAt: nextArchivedAt,
          unreadCount: nextArchivedAt ? 0 : existingConversation.unreadCount,
        };

        set((currentState) => ({
          conversations: currentState.conversations.map((conversation) =>
            conversation.id === conversationId ? optimisticConversation : conversation,
          ),
        }));

        try {
          const response = await authenticatedFetch(`/api/conversations/${conversationId}/archive`, {
            method: existingConversation.archivedAt ? 'DELETE' : 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders(),
            },
          });
          const data = await readApiPayload(response);
          if (!response.ok) {
            throw new Error(getApiErrorMessage(data));
          }

          const storedConversation = normalizeConversationApiPayload(data, optimisticConversation);
          set((currentState) => ({
            conversations: currentState.conversations.map((conversation) =>
              conversation.id === conversationId ? storedConversation : conversation,
            ),
            error: null,
          }));
        } catch (error) {
          set((currentState) => ({
            conversations: currentState.conversations.map((conversation) =>
              conversation.id === conversationId ? previousConversation : conversation,
            ),
            error: error instanceof Error ? error.message : 'Failed to update archived conversation',
          }));
        }
      },

      hideConversation: async (conversationId) => {
        const state = get();
        const existingConversation = state.conversations.find((conversation) => conversation.id === conversationId) ?? null;
        if (!existingConversation || !existingConversation.isGroup) {
          return;
        }

        const previousConversation = existingConversation;
        const optimisticConversation: Conversation = {
          ...existingConversation,
          isHidden: true,
          isUnread: false,
          unreadCount: 0,
        };

        set((currentState) => ({
          conversations: currentState.conversations.map((conversation) =>
            conversation.id === conversationId ? optimisticConversation : conversation,
          ),
        }));

        try {
          const response = await authenticatedFetch(`/api/conversations/${conversationId}/hide`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders(),
            },
          });
          const data = await readApiPayload(response);
          if (!response.ok) {
            throw new Error(getApiErrorMessage(data));
          }

          // Hide endpoint returns minimal { hidden: true }, keep optimistic state
        } catch (error) {
          set((currentState) => ({
            conversations: currentState.conversations.map((conversation) =>
              conversation.id === conversationId ? previousConversation : conversation,
            ),
            error: error instanceof Error ? error.message : 'Failed to hide conversation',
          }));
        }
      },

      unhideConversation: async (conversationId) => {
        const state = get();
        const existingConversation = state.conversations.find((conversation) => conversation.id === conversationId) ?? null;
        if (!existingConversation) {
          return;
        }

        const previousConversation = existingConversation;
        const optimisticConversation: Conversation = {
          ...existingConversation,
          isHidden: false,
        };

        set((currentState) => ({
          conversations: currentState.conversations.map((conversation) =>
            conversation.id === conversationId ? optimisticConversation : conversation,
          ),
        }));

        try {
          const response = await authenticatedFetch(`/api/conversations/${conversationId}/hide`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders(),
            },
          });
          const data = await readApiPayload(response);
          if (!response.ok) {
            throw new Error(getApiErrorMessage(data));
          }

          // Unhide endpoint returns minimal { hidden: false }, keep optimistic state
        } catch (error) {
          set((currentState) => ({
            conversations: currentState.conversations.map((conversation) =>
              conversation.id === conversationId ? previousConversation : conversation,
            ),
            error: error instanceof Error ? error.message : 'Failed to unhide conversation',
          }));
        }
      },

      markConversationUnread: async (conversationId) => {
        const state = get();
        const existingConversation = state.conversations.find((conversation) => conversation.id === conversationId) ?? null;
        if (!existingConversation) {
          return;
        }

        const previousConversation = existingConversation;
        const optimisticConversation: Conversation = {
          ...existingConversation,
          isUnread: true,
          // Keep original unreadCount — visual unread state is driven by isUnread flag,
          // not inflated count. This avoids inflating agent category totalUnread badges.
        };

        set((currentState) => ({
          conversations: currentState.conversations.map((conversation) =>
            conversation.id === conversationId ? optimisticConversation : conversation,
          ),
        }));

        try {
          const response = await authenticatedFetch(`/api/conversations/${conversationId}/mark-unread`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders(),
            },
          });
          const data = await readApiPayload(response);
          if (!response.ok) {
            throw new Error(getApiErrorMessage(data));
          }

          // Mark-unread endpoint returns minimal { unread: true }, keep optimistic state
        } catch (error) {
          set((currentState) => ({
            conversations: currentState.conversations.map((conversation) =>
              conversation.id === conversationId ? previousConversation : conversation,
            ),
            error: error instanceof Error ? error.message : 'Failed to mark conversation as unread',
          }));
        }
      },

      toggleActiveTagFilter: (tagId) => {
        set((state) => ({
          activeTagFilterIds: state.activeTagFilterIds.includes(tagId)
            ? state.activeTagFilterIds.filter((currentTagId) => currentTagId !== tagId)
            : [...state.activeTagFilterIds, tagId],
        }));
      },

      clearActiveTagFilters: () => {
        set({ activeTagFilterIds: [] });
      },

      setTagMatchMode: (mode) => {
        set({ tagMatchMode: mode });
      },

      setShowArchived: (showArchived) => {
        set({ showArchived });
      },

      setShowHidden: (showHidden) => {
        set({ showHidden });
      },

      searchConversations: async (query, filters = {}) => {
        const requestBoundary = captureOwnerScopedRequestBoundary();
        const trimmedQuery = query.trim();
        if (trimmedQuery.length < 2) {
          set({
            searchResults: [],
            searchTotal: 0,
            searchHasMore: false,
            searchError: null,
            isSearchingConversations: false,
          });
          return [];
        }

        set({ isSearchingConversations: true, searchError: null });

        if (!requestBoundary.ownerKey) {
          set({ isSearchingConversations: false });
          return [];
        }

        try {
          const state = get();
          const params = new URLSearchParams({ q: trimmedQuery });
          if (filters.agentId) params.set('agentId', filters.agentId);
          if (filters.from) params.set('from', filters.from);
          if (filters.to) params.set('to', filters.to);
          const tagIds = filters.tagIds?.length ? filters.tagIds : state.activeTagFilterIds;
          if (tagIds.length) params.set('tagIds', tagIds.join(','));
          params.set('tagMatch', filters.tagMatchMode ?? state.tagMatchMode);
          params.set('page', String(filters.page ?? 1));

          const response = await authenticatedFetch(`/api/search?${params.toString()}`, {
            headers: getAuthHeaders(),
          });
          if (!requestBoundary.isCurrent()) {
            return [];
          }

          const data = await readApiPayload(response);
          if (!requestBoundary.isCurrent()) {
            return [];
          }

          if (!response.ok) {
            throw new Error(getApiErrorMessage(data));
          }

          const payload = normalizeSearchResultsPayload(data);
          if (!requestBoundary.isCurrent()) {
            return [];
          }

          set((currentState) => ({
            searchResults: filters.append ? [...currentState.searchResults, ...payload.results] : payload.results,
            searchTotal: payload.total,
            searchHasMore: payload.hasMore,
            isSearchingConversations: false,
            searchError: null,
          }));
          return payload.results;
        } catch (error) {
          if (!requestBoundary.isCurrent()) {
            return [];
          }

          set({
            searchResults: [],
            searchTotal: 0,
            searchHasMore: false,
            isSearchingConversations: false,
            searchError: error instanceof Error ? error.message : 'Failed to search conversations',
          });
          return [];
        }
      },

      clearSearchResults: () => {
        set({ searchResults: [], searchTotal: 0, searchHasMore: false, searchError: null });
      },

      sendMessage: async (conversationId: string, content: string, attachments?: Array<File | Attachment>) => {
        if (useAuthStore.getState().viewAsUser) {
          set({ error: 'Read-only view: you cannot send messages while viewing another user\'s chats.' });
          return;
        }
        await dispatchUserMessage({
          get,
          set,
          conversationId,
          content,
          attachmentsInput: attachments,
        });
      },

      sendSuggestion: (text: string, conversationId?: string) => {
        const trimmedText = String(text || '').trim();
        const targetConversationId = String(conversationId || get().activeConversationId || '').trim();

        if (!trimmedText || !targetConversationId) {
          return;
        }

        set((state) => ({
          composerDrafts: {
            ...state.composerDrafts,
            [targetConversationId]: trimmedText,
          },
          suggestionDispatchVersion: {
            ...state.suggestionDispatchVersion,
            [targetConversationId]: (state.suggestionDispatchVersion[targetConversationId] || 0) + 1,
          },
        }));
      },

      clearComposerDraft: (conversationId: string) => {
        if (!conversationId) {
          return;
        }

        set((state) => {
          if (!state.composerDrafts[conversationId]) {
            return {};
          }

          const nextDrafts = { ...state.composerDrafts };
          delete nextDrafts[conversationId];
          return { composerDrafts: nextDrafts };
        });
      },

      retryMessage: async (messageId: string) => {
        const now = Date.now();
        const lastAttempt = lastRetryAttemptAt.get(messageId) ?? 0;
        if (now - lastAttempt < 1000) {
          return;
        }
        lastRetryAttemptAt.set(messageId, now);

        const entry = findMessageById(get().messages, messageId);
        if (!entry || entry.message.role !== 'user' || entry.message.status !== 'failed') {
          return;
        }

        const retryFiles = pendingUploadFiles.get(messageId);
        await dispatchUserMessage({
          get,
          set,
          conversationId: entry.conversationId,
          content: entry.message.content,
          attachmentsInput: retryFiles ?? entry.message.attachments ?? [],
          existingMessageId: messageId,
        });
      },

      regenerateMessage: async (messageId: string) => {
        const entry = findMessageById(get().messages, messageId);
        if (!entry || entry.message.role !== 'assistant') {
          return;
        }

        const conversationMessages = get().messages[entry.conversationId] || [];
        const assistantIndex = conversationMessages.findIndex((message) => message.id === messageId);
        if (assistantIndex < 0) {
          return;
        }

        const precedingUserMessage = [...conversationMessages.slice(0, assistantIndex)]
          .reverse()
          .find((message) => message.role === 'user');

        if (!precedingUserMessage) {
          return;
        }

        get().deleteMessage(messageId);
        try {
          await deletePersistedConversationMessage(entry.conversationId, messageId);
        } catch (error) {
          console.warn('Failed to remove assistant message before regeneration:', error);
        }

        await dispatchUserMessage({
          get,
          set,
          conversationId: entry.conversationId,
          content: precedingUserMessage.content,
          attachmentsInput: precedingUserMessage.attachments ?? [],
          existingMessageId: precedingUserMessage.id,
        });
      },

      editMessage: async (messageId: string, content: string) => {
        const trimmedContent = content.trim();
        const entry = findMessageById(get().messages, messageId);
        if (!entry || entry.message.role !== 'user' || !trimmedContent) {
          return;
        }

        const conversation = get().conversations.find((item) => item.id === entry.conversationId) ?? null;
        if (conversation?.isGroup) {
          get().setError('Editing is currently available for direct conversations only.');
          return;
        }

        const existingMessages = get().messages[entry.conversationId] || [];

        try {
          const result = await editPersistedConversationMessage(entry.conversationId, messageId, trimmedContent);
          const removedMessageIds = new Set(result.removedMessageIds);

          set((state) => ({
            messages: {
              ...state.messages,
              [entry.conversationId]: (state.messages[entry.conversationId] || [])
                .filter((message) => !removedMessageIds.has(message.id))
                .map((message) => (message.id === messageId ? result.message : message)),
            },
            error: null,
          }));

          await dispatchUserMessage({
            get,
            set,
            conversationId: entry.conversationId,
            content: trimmedContent,
            attachmentsInput: result.message.attachments ?? entry.message.attachments ?? [],
            existingMessageId: messageId,
          });
        } catch (error) {
          set((state) => ({
            messages: {
              ...state.messages,
              [entry.conversationId]: existingMessages,
            },
            error: error instanceof Error ? error.message : 'Failed to edit message',
          }));
          throw error;
        }
      },

      rateMessage: async (messageId: string, rating: 'up' | 'down') => {
        const entry = findMessageById(get().messages, messageId);
        if (!entry || entry.message.role !== 'assistant') {
          return;
        }

        const previousMessage = entry.message;
        const existingMetadata = previousMessage.metadata && typeof previousMessage.metadata === 'object'
          ? previousMessage.metadata
          : {};

        get().updateMessage(messageId, {
          metadata: {
            ...existingMetadata,
            rating,
          },
        });

        try {
          const storedMessage = await ratePersistedConversationMessage(entry.conversationId, messageId, rating);
          get().updateMessage(messageId, storedMessage);
          get().setError(null);
        } catch (error) {
          get().updateMessage(messageId, previousMessage);
          get().setError(error instanceof Error ? error.message : 'Failed to save message rating');
        }
      },

      stopGenerating: (conversationId: string) => {
        abortConversationStreams(conversationId);
        get().setTyping(conversationId, false);
      },

      cancelPendingJob: (jobId: string) => {
        const entry = findMessageByJobId(get().messages, jobId);
        if (!entry) {
          return;
        }

        const poller = pendingJobPollers.get(jobId);
        if (poller) {
          poller.cancelled = true;
        }
        stopPendingJobPolling(jobId);
        patchLocalMessage(set, entry.message.id, {
          metadata: {
            ...(entry.message.metadata && typeof entry.message.metadata === 'object' ? entry.message.metadata : {}),
            asyncJob: {
              ...entry.asyncJob,
              status: 'cancelled',
              progressMessage: 'Updates paused on this device. The job may still finish in the background.',
              assistantMessageId: entry.asyncJob.assistantMessageId ?? entry.message.id,
            },
          },
        });
        get().setTyping(entry.conversationId, false);
      },

      resumePendingJob: (jobId: string) => {
        const entry = findMessageByJobId(get().messages, jobId);
        if (!entry) {
          return;
        }

        const assistantMessageId = entry.asyncJob.assistantMessageId ?? entry.message.id;
        patchLocalMessage(set, entry.message.id, {
          metadata: {
            ...(entry.message.metadata && typeof entry.message.metadata === 'object' ? entry.message.metadata : {}),
            asyncJob: {
              ...entry.asyncJob,
              status: 'processing',
              progressMessage: entry.asyncJob.progressMessage && entry.asyncJob.progressMessage !== 'Updates paused on this device. The job may still finish in the background.'
                ? entry.asyncJob.progressMessage
                : 'Still working, reconnecting for an update...',
              assistantMessageId,
            },
          },
        });

        startPendingJobPolling({
          get,
          set,
          jobId,
          assistantMessageId,
          conversationId: entry.conversationId,
          userMessageId: entry.asyncJob.userMessageId ?? undefined,
        });
      },

      resumePendingJobs: () => {
        const pendingEntries = getPendingAsyncJobEntries(get().messages);
        const activeJobIds = new Set(pendingEntries.map((entry) => entry.asyncJob.jobId));

        pendingJobPollers.forEach((_, jobId) => {
          if (!activeJobIds.has(jobId)) {
            stopPendingJobPolling(jobId);
          }
        });

        pendingEntries.forEach(({ message, asyncJob }) => {
          const jobId = asyncJob.jobId;
          const assistantMessageId = asyncJob.assistantMessageId ?? message.id;
          const existing = pendingJobPollers.get(jobId);
          if (existing && existing.assistantMessageId === assistantMessageId && existing.conversationId === message.conversationId && !existing.cancelled) {
            return;
          }

          startPendingJobPolling({
            get,
            set,
            jobId,
            assistantMessageId,
            conversationId: message.conversationId,
            userMessageId: asyncJob.userMessageId ?? undefined,
          });
        });

        // Server truth: pick up running jobs this client doesn't know about
        // (refresh with cleared storage, second device). Throttled internally.
        void get().syncActiveJobsFromServer();
      },

      syncActiveJobsFromServer: async (options?: { force?: boolean }) => {
        if (typeof window === 'undefined') {
          return;
        }
        if (!useAuthStore.getState().isAuthenticated) {
          return;
        }

        const now = Date.now();
        if (!options?.force && now - lastActiveJobsServerSyncMs < ACTIVE_JOBS_SERVER_SYNC_MIN_INTERVAL_MS) {
          return;
        }
        lastActiveJobsServerSyncMs = now;

        const requestSessionAsyncBoundaryVersion = getSessionAsyncBoundaryVersion();

        try {
          const jobs = await requestActiveJobs();
          if (!isSessionAsyncBoundaryCurrent(requestSessionAsyncBoundaryVersion)) {
            return;
          }

          for (const job of jobs) {
            const jobId = String(job.jobId || '').trim();
            const conversationId = String(job.conversationId || '').trim();
            if (!jobId || !conversationId) {
              continue;
            }
            // Already tracked locally — the placeholder/poller path owns it.
            if (pendingJobPollers.has(jobId) || findMessageByJobId(get().messages, jobId)) {
              continue;
            }

            const assistantMessageId = job.assistantMessageId ? String(job.assistantMessageId) : `async-job-${jobId}`;
            const existingEntry = findMessageById(get().messages, assistantMessageId);
            // The final assistant message already arrived via history — no card.
            if (existingEntry && String(existingEntry.message.content || '').trim()) {
              continue;
            }

            const startedAt = typeof job.startedAt === 'string' && job.startedAt.trim() ? job.startedAt : new Date().toISOString();
            const userMessageId = job.userMessageId ? String(job.userMessageId) : null;

            upsertLocalConversationMessage(set, conversationId, {
              id: assistantMessageId,
              conversationId,
              role: 'assistant',
              content: '',
              createdAt: startedAt,
              timestamp: startedAt,
              status: 'pending',
              agentId: typeof job.agentId === 'string' && job.agentId ? job.agentId : undefined,
              metadata: {
                asyncJob: {
                  jobId,
                  status: 'processing',
                  progressMessage: typeof job.message === 'string' && job.message.trim() ? job.message : 'Still working…',
                  startedAt,
                  elapsedMs: Math.max(0, Number(job.elapsedMs || 0) || 0),
                  userMessageId,
                  assistantMessageId,
                } as AsyncJobMetadata,
              },
              reactions: { counts: {}, userReactions: [], reactors: {} },
            } as Message);

            startPendingJobPolling({
              get,
              set,
              jobId,
              assistantMessageId,
              conversationId,
              userMessageId,
            });
          }
        } catch {
          // Best-effort: offline or auth hiccups just skip this sync round —
          // the next resume (focus/online/load) retries.
        }
      },

      rebuildAsyncJobSummaries: () => {
        set({ asyncJobSummaries: buildAsyncJobSummariesByConversation(get().messages) });
      },

      addMessage: (message: Message) => {
        const normalized = normalizeMessage(message);

        set((state) => {
          const conversationMessages = state.messages[normalized.conversationId] || [];
          const existingIndex = conversationMessages.findIndex((item) => item.id === normalized.id);
          let nextMessages: Message[];

          if (existingIndex >= 0) {
            nextMessages = [...conversationMessages];
            nextMessages[existingIndex] = {
              ...nextMessages[existingIndex],
              ...normalized,
            };
          } else {
            nextMessages = sortMessages([...conversationMessages, normalized]);
          }

          return {
            messages: {
              ...state.messages,
              [normalized.conversationId]: nextMessages,
            },
          };
        });

        get().updateConversationLastMessage(normalized);
        maybeNotifyAssistantMessage(normalized, get().conversations, get().agents, get().activeConversationId);
      },

      updateMessage: (messageId: string, updates: Partial<Message>) => {
        let updatedMessage: Message | undefined;

        set((state) => {
          const nextMessages = { ...state.messages };

          for (const conversationId of Object.keys(nextMessages)) {
            const items = nextMessages[conversationId];
            const index = items.findIndex((message) => message.id === messageId);
            if (index >= 0) {
              const merged = normalizeMessage({ ...items[index], ...updates }, conversationId);
              nextMessages[conversationId] = [...items];
              nextMessages[conversationId][index] = merged;
              updatedMessage = merged;
              break;
            }
          }

          return { messages: nextMessages };
        });

        if (updatedMessage) {
          get().updateConversationLastMessage(updatedMessage);
          maybeNotifyAssistantMessage(updatedMessage, get().conversations, get().agents, get().activeConversationId);
        }
      },

      deleteMessage: (messageId: string) => {
        pendingUploadFiles.delete(messageId);
        lastRetryAttemptAt.delete(messageId);

        set((state) => {
          const nextMessages = { ...state.messages };
          for (const conversationId of Object.keys(nextMessages)) {
            const filtered = nextMessages[conversationId].filter((message) => message.id !== messageId);
            if (filtered.length !== nextMessages[conversationId].length) {
              nextMessages[conversationId] = filtered;
              return { messages: nextMessages };
            }
          }
          return { messages: nextMessages };
        });
      },

      toggleReaction: (messageId: string, emoji: string) => {
        const authState = useAuthStore.getState();
        const effectiveUser = authState.impersonatedUser ?? authState.user;
        const userName = effectiveUser?.name || 'You';
        let targetConversationId: string | null = null;
        let previousMessage: Message | null = null;

        set((state) => {
          const nextMessages = { ...state.messages };

          for (const conversationId of Object.keys(nextMessages)) {
            const index = nextMessages[conversationId].findIndex((message) => message.id === messageId);
            if (index < 0) continue;

            targetConversationId = conversationId;
            const message = nextMessages[conversationId][index];
            previousMessage = message;
            const reactions = message.reactions ?? { counts: {}, userReactions: [], reactors: {} };
            const userReactions = new Set(reactions.userReactions);
            const counts = { ...reactions.counts };
            const reactors = { ...(reactions.reactors ?? {}) };
            const names = new Set(reactors[emoji] ?? []);

            if (userReactions.has(emoji)) {
              userReactions.delete(emoji);
              counts[emoji] = Math.max((counts[emoji] ?? 1) - 1, 0);
              names.delete(userName);
            } else {
              userReactions.add(emoji);
              counts[emoji] = (counts[emoji] ?? 0) + 1;
              names.add(userName);
            }

            if (counts[emoji] <= 0) {
              delete counts[emoji];
              delete reactors[emoji];
            } else {
              reactors[emoji] = Array.from(names);
            }

            nextMessages[conversationId] = [...nextMessages[conversationId]];
            nextMessages[conversationId][index] = {
              ...message,
              reactions: {
                counts,
                userReactions: Array.from(userReactions),
                reactors,
              },
            };
            break;
          }

          return { messages: nextMessages };
        });

        if (!targetConversationId) {
          return;
        }

        void (async () => {
          try {
            const response = await authenticatedFetch(`/api/conversations/${targetConversationId}/messages/${messageId}/reactions`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders(),
              },
              body: JSON.stringify({ emoji }),
            });

            const payload = await readApiPayload(response);
            if (!response.ok) {
              throw new Error(getApiErrorMessage(payload));
            }

            const rawMessage = payload && typeof payload === 'object' && 'data' in payload ? payload.data : payload;
            if (rawMessage) {
              set((state) => ({
                messages: {
                  ...state.messages,
                  [targetConversationId as string]: (state.messages[targetConversationId as string] || []).map((item) => (
                    item.id === messageId ? normalizeMessage(rawMessage, targetConversationId as string) : item
                  )),
                },
              }));
            }
          } catch (error) {
            if (previousMessage && targetConversationId) {
              set((state) => ({
                messages: {
                  ...state.messages,
                  [targetConversationId as string]: (state.messages[targetConversationId as string] || []).map((item) => (
                    item.id === messageId ? previousMessage as Message : item
                  )),
                },
                error: error instanceof Error ? error.message : 'Failed to update reaction',
              }));
            }
          }
        })();
      },

      togglePin: (messageId: string) => {
        let targetConversationId: string | null = null;
        let previousMessage: Message | null = null;
        let nextPinned = false;

        set((state) => {
          const nextMessages = { ...state.messages };
          for (const conversationId of Object.keys(nextMessages)) {
            const index = nextMessages[conversationId].findIndex((message) => message.id === messageId);
            if (index < 0) continue;
            targetConversationId = conversationId;
            const message = nextMessages[conversationId][index];
            previousMessage = message;
            nextPinned = !message.isPinned;
            nextMessages[conversationId] = [...nextMessages[conversationId]];
            nextMessages[conversationId][index] = {
              ...message,
              isPinned: nextPinned,
            };
            break;
          }
          return { messages: nextMessages };
        });

        if (!targetConversationId) {
          return;
        }

        void (async () => {
          try {
            const response = await authenticatedFetch(`/api/conversations/${targetConversationId}/messages/${messageId}/pin`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders(),
              },
              body: JSON.stringify({ pinned: nextPinned }),
            });

            const payload = await readApiPayload(response);
            if (!response.ok) {
              throw new Error(getApiErrorMessage(payload));
            }

            const rawMessage = payload && typeof payload === 'object' && 'data' in payload ? payload.data : payload;
            if (rawMessage) {
              set((state) => ({
                messages: {
                  ...state.messages,
                  [targetConversationId as string]: sortMessages((state.messages[targetConversationId as string] || []).map((item) => (
                    item.id === messageId ? normalizeMessage(rawMessage, targetConversationId as string) : item
                  ))),
                },
              }));
            }
          } catch (error) {
            if (previousMessage && targetConversationId) {
              set((state) => ({
                messages: {
                  ...state.messages,
                  [targetConversationId as string]: sortMessages((state.messages[targetConversationId as string] || []).map((item) => (
                    item.id === messageId ? previousMessage as Message : item
                  ))),
                },
                error: error instanceof Error ? error.message : 'Failed to pin message',
              }));
            }
          }
        })();
      },

      startStreaming: (conversationId: string, messageId: string) => {
        get().setTyping(conversationId, false);
        set((state) => ({
          streamingMessageIds: {
            ...state.streamingMessageIds,
            [conversationId]: messageId,
          },
          streamingTokens: {
            ...state.streamingTokens,
            [messageId]: [],
          },
        }));
      },

      addToken: (messageId: string, token: string) => {
        set((state) => {
          if (!Object.prototype.hasOwnProperty.call(state.streamingTokens, messageId)) {
            return state;
          }

          return {
            streamingTokens: {
              ...state.streamingTokens,
              [messageId]: [...(state.streamingTokens[messageId] || []), token],
            },
          };
        });
      },

      completeMessage: (messageId: string, conversationId: string, finalContent: string) => {
        const currentState = get();
        const isTrackedStream = currentState.streamingMessageIds[conversationId] === messageId
          || Object.prototype.hasOwnProperty.call(currentState.streamingTokens, messageId);

        if (!isTrackedStream) {
          return;
        }

        const timestamp = new Date().toISOString();
        get().addMessage({
          id: messageId,
          conversationId,
          role: 'assistant',
          content: finalContent,
          createdAt: timestamp,
          timestamp,
          status: 'sent',
          reactions: { counts: {}, userReactions: [], reactors: {} },
        });
        get().setTyping(conversationId, false);

        set((state) => {
          const nextTokens = { ...state.streamingTokens };
          delete nextTokens[messageId];
          const nextStreamingIds = { ...state.streamingMessageIds };
          if (nextStreamingIds[conversationId] === messageId) {
            delete nextStreamingIds[conversationId];
          }
          return {
            streamingTokens: nextTokens,
            streamingMessageIds: nextStreamingIds,
          };
        });
      },

      createConversation: async (agentId: string, title?: string) => {
        const conversation = fallbackConversation(agentId, title, get().agents);
        set((state) => ({
          conversations: [conversation, ...state.conversations.filter((item) => item.id !== conversation.id)],
          activeConversationId: conversation.id,
          messages: {
            ...state.messages,
            [conversation.id]: state.messages[conversation.id] ?? [],
          },
        }));

        try {
          const storedConversation = await persistConversation(conversation);
          set((state) => ({
            conversations: [storedConversation, ...state.conversations.filter((item) => item.id !== storedConversation.id)],
            activeConversationId: storedConversation.id,
            messages: {
              ...state.messages,
              [storedConversation.id]: state.messages[storedConversation.id] ?? state.messages[conversation.id] ?? [],
            },
          }));
          return storedConversation.id;
        } catch (error) {
          console.warn('Failed to persist conversation', error);
          return conversation.id;
        }
      },

      createGroupConversation: async (name: string, agentIds: string[]) => {
        const now = new Date().toISOString();
        const conversationId = crypto.randomUUID();
        const participants = createGroupParticipants(conversationId, agentIds, get().agents);
        const conversation: Conversation = {
          id: conversationId,
          title: name,
          groupName: name,
          type: 'GROUP',
          kind: 'group',
          isGroup: true,
          unreadCount: 0,
          participantCount: participants.length,
          participants,
          summary: 'Agents only respond when you @mention them.',
          createdAt: now,
          updatedAt: now,
          lastActivity: now,
        };

        set((state) => ({
          conversations: [conversation, ...state.conversations.filter((item) => item.id !== conversationId)],
          activeConversationId: conversationId,
          messages: {
            ...state.messages,
            [conversationId]: state.messages[conversationId] ?? [],
          },
        }));

        try {
          const storedConversation = await persistConversation(conversation);
          set((state) => ({
            conversations: [storedConversation, ...state.conversations.filter((item) => item.id !== storedConversation.id)],
            activeConversationId: storedConversation.id,
            messages: {
              ...state.messages,
              [storedConversation.id]: state.messages[storedConversation.id] ?? state.messages[conversationId] ?? [],
            },
          }));
          return storedConversation.id;
        } catch (error) {
          console.warn('Failed to persist group conversation', error);
          return conversationId;
        }
      },

      convertConversationToGroup: async (conversationId: string, name: string, memberEmails: string[]) => {
        const response = await authenticatedFetch(`/api/conversations/${conversationId}/convert-to-group`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({ groupName: name, members: memberEmails }),
        });

        const payload = await readApiPayload(response);
        if (!response.ok) {
          throw new Error(getApiErrorMessage(payload));
        }

        const rawConversation = payload && typeof payload === 'object'
          ? ('data' in payload ? (payload as any).data : 'conversation' in payload ? (payload as any).conversation : payload)
          : payload;
        const storedConversation = normalizeConversation(rawConversation ?? { id: conversationId });

        // Full replace (not a shallow patch) so isGroup/type/kind/participants
        // land together and the conversation moves to the Group Chats section.
        get().updateConversation(conversationId, storedConversation);
        get().setError(null);
      },

      renameConversation: async (conversationId: string, title: string) => {
        const trimmedTitle = title.trim();
        const existingConversation = getConversationById(get().conversations, conversationId);

        if (!existingConversation || !trimmedTitle || existingConversation.title === trimmedTitle) {
          return;
        }

        get().updateConversation(conversationId, { title: trimmedTitle });

        try {
          const storedConversation = await persistConversationUpdates(conversationId, { title: trimmedTitle });
          get().updateConversation(conversationId, storedConversation);
          get().setError(null);
        } catch (error) {
          get().updateConversation(conversationId, { title: existingConversation.title });
          get().setError(error instanceof Error ? error.message : 'Failed to rename conversation');
          throw error;
        }
      },

      updateConversation: (conversationId: string, updates: Partial<Conversation>) => {
        set((state) => ({
          conversations: state.conversations.map((conversation) =>
            conversation.id === conversationId ? { ...conversation, ...updates } : conversation,
          ),
        }));
      },

      deleteConversation: async (conversationId: string) => {
        try {
          await authenticatedFetch(`/api/conversations/${conversationId}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
          });
        } catch (error) {
          console.warn('Failed to delete conversation from server', error);
        }

        set((state) => ({
          conversations: state.conversations.filter((conversation) => conversation.id !== conversationId),
          messages: Object.fromEntries(Object.entries(state.messages).filter(([id]) => id !== conversationId)),
          activeConversationId: state.activeConversationId === conversationId ? null : state.activeConversationId,
        }));
      },

      setTyping: ((conversationIdOrTyping: string | boolean, maybeTyping?: boolean) => {
        if (typeof conversationIdOrTyping === 'boolean') {
          const typing = conversationIdOrTyping;
          set((state) => ({
            isTyping: typing,
            typing: state.activeConversationId
              ? {
                  ...state.typing,
                  [state.activeConversationId]: typing,
                }
              : state.typing,
          }));
          return;
        }

        const conversationId = conversationIdOrTyping;
        const typing = Boolean(maybeTyping);
        set((state) => ({
          isTyping: typing,
          typing: {
            ...state.typing,
            [conversationId]: typing,
          },
        }));
      }) as ChatState['setTyping'],

      updateConversationLastMessage: (message: Message) => {
        set((state) => {
          const messageTimestamp = getMessageTimestamp(message);
          const existing = state.conversations.find((conversation) => conversation.id === message.conversationId);

          if (!existing) {
            const title = generateConversationTitle(message.content || 'New Chat');
            const agentId = message.agentId ?? state.agents[0]?.id ?? DEFAULT_AGENT.id;
            const conversation: Conversation = {
              ...fallbackConversation(agentId, title, state.agents),
              id: message.conversationId,
              lastMessage: message,
              lastActivity: messageTimestamp,
              updatedAt: messageTimestamp,
              unreadCount: message.role === 'assistant' ? 1 : 0,
              messageCount: 1,
            };

            return {
              conversations: [conversation, ...state.conversations],
            };
          }

          const isUpdatingExistingLastMessage = existing.lastMessage?.id === message.id;
          const wasArchived = Boolean(existing.archivedAt);
          const updatedConversation: Conversation = {
            ...existing,
            title:
              !existing.isGroup && existing.title === 'New Chat' && message.role === 'user' && message.content
                ? generateConversationTitle(message.content)
                : existing.title,
            lastMessage: message,
            lastActivity: messageTimestamp,
            updatedAt: messageTimestamp,
            archivedAt: null,
            messageCount: isUpdatingExistingLastMessage
              ? existing.messageCount
              : (existing.messageCount ?? 0) + 1,
            unreadCount:
              message.role === 'assistant' && existing.id !== state.activeConversationId && !isUpdatingExistingLastMessage
                ? existing.unreadCount + 1
                : existing.unreadCount,
          };

          return {
            conversations: state.conversations
              .map((conversation) =>
                conversation.id === message.conversationId ? updatedConversation : conversation,
              )
              .filter((conversation) => !(state.showArchived && wasArchived && conversation.id === message.conversationId)),
          };
        });
      },

      setLoading: (loading: boolean) => set({ isLoading: loading }),
      setError: (error: string | null) => set({ error }),
      clearError: () => set({ error: null }),
      setVoiceEnabled: (voiceEnabled: boolean) => set({ voiceEnabled }),
      setSidebarConversationsEnabled: (enabled: boolean) => {
        setStoredSidebarConversationsEnabled(enabled);
        set({ sidebarConversationsEnabled: enabled });
      },
      toggleFavoriteAgent: async (agentId) => {
        const previousFavoriteAgentIds = get().favoriteAgentIds;
        const nextFavoriteAgentIds = previousFavoriteAgentIds.includes(agentId)
          ? previousFavoriteAgentIds.filter((id) => id !== agentId)
          : [...previousFavoriteAgentIds, agentId];

        get().setFavoriteAgentIds(nextFavoriteAgentIds);

        try {
          const response = await authenticatedFetch('/api/user/preferences', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders(),
            },
            body: JSON.stringify({ favoriteAgentIds: nextFavoriteAgentIds }),
          });
          const data = await readApiPayload(response);

          if (!response.ok) {
            throw new Error(getApiErrorMessage(data));
          }

          const preferences = data && typeof data === 'object' && 'data' in data
            ? (data as { data?: { favoriteAgentIds?: unknown } }).data
            : null;

          if (preferences && Array.isArray(preferences.favoriteAgentIds)) {
            get().setFavoriteAgentIds(normalizeFavoriteAgentIds(preferences.favoriteAgentIds));
          }

          set({ error: null });
        } catch (error) {
          get().setFavoriteAgentIds(previousFavoriteAgentIds);
          set({ error: error instanceof Error ? error.message : 'Failed to update favorite agents' });
        }
      },
    })),
    {
      name: 'ally-chat-store',
      storage: persistStorage,
      onRehydrateStorage: () => () => {
        useChatStore.getState().rebuildAsyncJobSummaries();
        useChatStore.getState().resumePendingJobs();
      },
      partialize: (state) => ({
        conversations: state.conversations,
        messages: state.messages,
        activeConversationId: state.activeConversationId,
        agents: state.agents,
        tags: state.tags,
        activeTagFilterIds: state.activeTagFilterIds,
        tagMatchMode: state.tagMatchMode,
        showArchived: state.showArchived,
        showHidden: state.showHidden,
        voiceEnabled: state.voiceEnabled,
        sidebarConversationsEnabled: state.sidebarConversationsEnabled,
        favoriteAgentIds: state.favoriteAgentIds,
        persistedSessionOwnerKey: state.persistedSessionOwnerKey,
      }),
    },
  ),
);

if (typeof useChatStore.subscribe === 'function') {
  useChatStore.subscribe(
    (state) => state.messages,
    (messages) => {
      const nextSummaries = buildAsyncJobSummariesByConversation(messages);
      const currentSummaries = useChatStore.getState().asyncJobSummaries;
      if (!areAsyncJobSummariesEqual(currentSummaries, nextSummaries)) {
        useChatStore.setState({ asyncJobSummaries: nextSummaries });
      }
    },
    { fireImmediately: true },
  );
}

if (typeof useAuthStore.subscribe === 'function') {
  useAuthStore.subscribe((state, previousState) => {
    const currentSessionOwnerKey = getSessionOwnerKey(state);
    const previousSessionOwnerKey = getSessionOwnerKey(previousState);

    if (!state.isAuthenticated) {
      clearSessionScopedAsyncState();

      const currentChatState = useChatStore.getState();
      const persistedSessionOwnerKey = currentChatState.persistedSessionOwnerKey ?? previousSessionOwnerKey ?? currentSessionOwnerKey;
      const retainedMessages = persistedSessionOwnerKey
        ? getRetainedAsyncJobMessages(currentChatState.messages)
        : {};

      useChatStore.setState(buildAuthBoundaryResetState(
        retainedMessages,
        hasRetainedAsyncJobMessages(retainedMessages) ? persistedSessionOwnerKey : null,
      ));
      return;
    }

    if (!currentSessionOwnerKey) {
      return;
    }

    const currentChatState = useChatStore.getState();
    if (
      currentChatState.persistedSessionOwnerKey
      && currentChatState.persistedSessionOwnerKey !== currentSessionOwnerKey
    ) {
      clearSessionScopedAsyncState();
      useChatStore.setState(buildAuthBoundaryResetState({}, currentSessionOwnerKey));
      return;
    }

    if (currentChatState.persistedSessionOwnerKey !== currentSessionOwnerKey) {
      useChatStore.setState({ persistedSessionOwnerKey: currentSessionOwnerKey });
    }
  });
}

export type { Agent, Attachment, Conversation, ConversationSearchResult, Message, Tag };
